// IMPORT React
import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

// IMPORT PACKAGES
import * as Yup from "yup";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { components } from "react-select";
import dayjs, { Dayjs } from "dayjs";
import parse from "html-react-parser";

// IMPORT CSS
import "../../../theme/style.scss";

// IMPORT UI COMPONENTS
import {
  AppBar,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  CustomAsyncSelect,
  CustomSelect,
  SimpleAsyncSelect,
} from "../../../components/CustomFields/CustomSelect";
import SkeletonWrapper from "../../../components/Skeleton/SkeletonWrapper";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { CustomTextField } from "../../../components/CustomFields/TextField";
// import { CustomSwitch } from "../../../components/CustomFields/CustomSwitch";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";

// IMPORT SERVICE
import { AlertType } from "../../../services/models/AlertType";
import { EncryptDecryptService } from "../../../services/common/EncryptionService";
import {
  GetMedicineDetail,
  GetMedicineList,
} from "../../../services/MedicineService";
import { PreferencesContext } from "../../../PreferenceContext";
import CustomBreadcrumb from "../../../components/BreadcrumbsComponent/CustomBreadcrumbs";
import { PAGE_SIZE } from "../../../services/common/const";
import DraggableList from "../../../components/Draggable/DraggableList";
import CustomTimePicker from "../../../components/TimePicker/CustomTimePicker";
import {
  createInputPropsForNumberField,
  underModules,
} from "../../../shared/data";
import { GetMedicineUsedAsList } from "../../../services/MedicineUsedAsService";
import {
  // frequency,
  // unitsDurationOfTreatment,
  // unitsForPrint,
  // unitsOfDiluant,
  // unitsOfDose,
  unitsOfPerWeightBSA,
  // unitsOfTime,
  // unitsPerInterval,
} from "../../../shared/Data/MedicineDose";
import {
  //  getLabel,
  handleFocus,
} from "../../../shared/CommonFunction";
// import { GetMedicineDiluentNameList } from "../../../services/MedicineDiluentNameService";
import { GetMedicineRelationToFoodList } from "../../../services/MedicineRelationToFoodService";
import { GetMedicineRouteOfAdministrationList } from "../../../services/MedicineRouteOfAdministrationService";
import { GetMedicineIndicationForSOSMedicineList } from "../../../services/MedicineIndicationForSOSMedicineService";
import {
  CreateMedicineDose,
  GetMedicineDoseDetail,
  UpdateMedicineDose,
} from "../../../services/MedicineDoseService";
import {
  GetInstructionDetail,
  GetInstructionList,
} from "../../../services/InstructionService";
import { GetSpecialityList } from "../../../services/SpecialityService";
import { GetMedicineGenericNameList } from "../../../services/MedicineGenericNameService";
import MedicineUsedAsSave from "../MedicineUsedAs/MedicineUsedAsSave";
import MedicineRelationToFoodSave from "../MedicineRelationtoFood/MedicineRelationtoFoodSave";
import MedicineDiluentNameSave from "../MedicineDiluentName/MedicineDiluentNameSave";
import MedicineRouteOfAdministrationSave from "../MedicineRouteofAdministration/MedicinerouteofadministrationSave";
import MedicineIndicationForSOSMedicineSave from "../MedicineIndicationforSOSmedicine/MedicineIndicationforSOSmedicineSave";
import MedicineGenericNameSave from "../MedicineGenericName/MedicineGenericNameSave";
import Medicine2Save from "../Medicine/MedicineSave";
import InstructionSave from "../../Instruction/InstructionSave";
import CustomTooltip from "../../../components/ToolTip/CustomToolTip";
import {
  GetStrengthUnitList,
  GetFrequencyUnitList,
  GetPerIntervalUnitList,
  GetTreatmentDurationUnitList,
} from "../../../services/MasterService";
import { GetDepartmentList } from "../../../services/DepartmentService";

// SCHEMA FOR OPTION
interface selectOption {
  label: string;
  value: string;
}

// SCHEMA FOR PARAMETER
interface Parameter {
  specialityId: string;
  departmentId: string;
  medicineDoseId: string;
  consumerId: string;
  orgId: string;
  branchId: string;
  pageIndex: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
  tabType: number;
  mode: string;
  saveType: string;
  medicineRelationToFoodTypeCode: string;
}

// DEFAULT PARAMS -- ADD HERE IF YOU NEED NEW PARAMETER
const parameters: Parameter = {
  medicineDoseId: "",
  consumerId: "",
  orgId: "",
  branchId: "",
  pageIndex: 1,
  pageSize: PAGE_SIZE,
  sortColumn: "",
  sortDirection: "",
  searchValue: "",
  tabType: 0,
  mode: "DROPDOWN",
  saveType: "SAVE",
  medicineRelationToFoodTypeCode: "MEDICINE_RELATION_TO_FOOD",
  specialityId: "",
  departmentId: "",
};

//SCHEMA FOR DETAIL COLLECTION
interface IMedicineDoseInstruction {
  medicineDoseInstructionId: number;
  instructionId: number;
  instructionData: string;
  displayOrder: number;
  isDeleted: boolean;
  mode: string;
}

//SCHEMA FOR DETAIL MEDICINE COLLECTION
interface IMedicineDoseMedicine {
  medicineDoseMedicineId: number;
  displayOrder: number;
  medicineId: number;
  label: string;
  strengthLabel: string;
  value: number;
  isDeleted: boolean;
  mode: string;
  dosePrintUnitName: string;
  doseDecidingContent: string;
  packageAmountOfContent: number;
  packageUnitPerVolume: string;
  packageTypeName: string;
  genericName: string;
}

// SCHEMA FOR FORM
interface formInitialValueTypes {
  medicineDoseId: number;
  consumerId: number;
  orgId: number;
  branchId: number;
  medicineDoseUnderModule: selectOption | null;
  medicineDoseTitle: string;
  medicineGenericId: selectOption | null;
  medicineGenericName: string;
  doseDecidingContent: string | null;
  // medicineId: selectOption | null;
  medicineUsedAsId: selectOption | null;
  medicineUsedAsName: string | null;
  departmentId: selectOption | null;
  specialityId: selectOption | null;
  weightBsaDependentMinDoseValue: number | null;
  weightBsaDependentDesiredDoseValue: number | null;
  weightBsaDependentDesiredDoseUnit: selectOption | null;
  weightBsaDependentDesiredDoseUnitName: string | null;
  weightBsaDependentPerWeightBsaUnit: selectOption | null;
  weightBsaDependentPerWeightBsaUnitName: string | null;
  weightBsaDependentPerIntervalUnit: selectOption | null;
  weightBsaDependentPerIntervalUnitName: string | null;
  weightBsaDependentMaxDoseValue: number | null;
  fixedDoseValue: number | null;
  fixedDoseUnit: selectOption | null;
  fixedDoseUnitName: string | null;
  fixedDosePerIntervalUnit: selectOption | null;
  fixedDosePerIntervalUnitName: string | null;
  maxFixedDoseValue: number | null;
  maxFixedDoseUnit: selectOption | null;
  maxFixedDoseUnitName: string | null;
  maxFixedDosePerIntervalUnit: selectOption | null;
  maxFixedDosePerIntervalUnitName: string | null;
  isDilutionNeeded: boolean;
  diluentNameId: selectOption | null;
  diluentName: string | null;
  diluentValue: number | null;
  diluentUnit: string;
  diluentUnitName: string | null;
  dosePrintUnit: selectOption | null;
  dosePrintUnitName: string | null;
  durationOfTreatmentValue: number | null;
  durationOfTreatmentUnit: selectOption | null;
  durationOfTreatmentUnitName: string | null;
  relationToFoodTimeGapValue: number | null;
  relationToFoodTimeGapUnit: string;
  relationToFoodTimeGapUnitName: string | null;
  relationToFoodId: selectOption | null;
  relationToFoodName: string | null;
  doseFrequency: selectOption | null;
  doseFrequencyName: string | null;
  routeOfAdministrationId: selectOption | null;
  routeOfAdministrationName: string | null;
  indicationOfSosMedicineId: selectOption | null;
  indicationOfSosMedicineName: string | null;
  isMorningScheduleEnabled: boolean;
  morningScheduleTime: Dayjs | null;
  isAfternoonScheduleEnabled: boolean;
  afternoonScheduleTime: Dayjs | null;
  isEveningScheduleEnabled: boolean;
  eveningScheduleTime: Dayjs | null;
  isNightScheduleEnabled: boolean;
  nightScheduleTime: Dayjs | null;
  scheduleInstruction: string;
  medicineDoseInstructionColl: Array<IMedicineDoseInstruction>;
  medicineDoseMedicineColl: Array<IMedicineDoseMedicine>;
  fileteredMedicineDoseMedicineColl: Array<IMedicineDoseMedicine>;
  previousMedicineDoseId: number | null;
  nextMedicineDoseId: number | null;
  isDeleted: boolean;
}

interface IMedicineDoseSaveProps {
  isForPopUp?: boolean;
  handleClosePopUp: (code: string, data?: any) => void;
  name: string;
  genericNameObject?: any;
  popUpMedicineDoseId?: number;
  isFrom?: string;
  medicineDoseMedicineColl?: Array<IMedicineDoseMedicine>;
}

// SLOT VALIDATION
export const slotValidationFn = (context) => {
  const {
    doseFrequency,
    isMorningScheduleEnabled,
    isAfternoonScheduleEnabled,
    isEveningScheduleEnabled,
    isNightScheduleEnabled,
  } = context.parent;

  if (!doseFrequency) return true;

  const maxAllowed = Math.floor(
    doseFrequency.frequencyConversationValue /
      (doseFrequency.frequencyConversationUnit || 1),
  );

  const selectedCount = [
    isMorningScheduleEnabled,
    isAfternoonScheduleEnabled,
    isEveningScheduleEnabled,
    isNightScheduleEnabled,
  ].filter(Boolean).length;
  if (maxAllowed > 4) {
    return selectedCount === 0;
  }
  // return maxAllowed === selectedCount;
  return selectedCount === 0 || selectedCount === maxAllowed;
};

export default function MedicineDoseSave(Props: IMedicineDoseSaveProps) {
  //PROPS
  const {
    isForPopUp = false,
    handleClosePopUp,
    name,
    genericNameObject = null,
    popUpMedicineDoseId = 0,
    isFrom = "",
  } = Props;

  // HOOKS AND CONTEXT
  const navigate = useNavigate();
  const theme = useTheme();
  const edService = new EncryptDecryptService();
  const { userContext, updateUserContext } = useContext(PreferencesContext);
  function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
  }

  // GETTING PARAMS
  let medicineDoseId = 0;
  let copyMedicineDoseId = 0;
  let urlParams = useQuery();

  if (!isForPopUp) {
    let curRawMedicineId = urlParams.get("q");
    let curRawCopyMedicineDoseId = urlParams.get("c");
    if (curRawMedicineId !== null && curRawMedicineId !== undefined) {
      let decId = edService.decrypt(curRawMedicineId);
      if (decId !== "" && decId !== null && decId !== undefined) {
        medicineDoseId = +decId;
      }
      if (
        curRawCopyMedicineDoseId !== null &&
        curRawCopyMedicineDoseId !== undefined
      ) {
        let decId2 = edService.decrypt(curRawCopyMedicineDoseId);
        if (decId2 !== "" && decId2 !== null && decId2 !== undefined) {
          copyMedicineDoseId = +decId2;
        }
      }
    } else {
      if (urlParams.size > 0) {
        // navigate("/not-found");
      }
    }
  } else {
    if (popUpMedicineDoseId > 0) {
      medicineDoseId = popUpMedicineDoseId;
    }
  }
  // DEFAULT SCHEMA VALUE FOR FORM
  const initMedicineDoseData: formInitialValueTypes = {
    medicineDoseId: 0,
    consumerId: 0,
    orgId: 0,
    branchId: 0,
    medicineDoseUnderModule: {
      label: "OPD",
      value: "OPD",
    },
    medicineDoseTitle: isForPopUp ? name?.toUpperCase() : "",
    // medicineId: null,
    medicineUsedAsId: null,
    medicineUsedAsName: null,
    departmentId:
      userContext?.departmentId > 0
        ? {
            label: userContext?.departmentName || "",
            value: userContext?.departmentId?.toString(),
          }
        : null,
    specialityId:
      userContext?.specialityId > 0
        ? {
            label: userContext?.specialityName || "",
            value: userContext?.specialityId?.toString(),
          }
        : null,
    weightBsaDependentMinDoseValue: null,
    weightBsaDependentDesiredDoseValue: null,
    weightBsaDependentDesiredDoseUnit: {
      label: "mg",
      value: "MG",
    },
    weightBsaDependentDesiredDoseUnitName: "mg",
    weightBsaDependentPerWeightBsaUnit: {
      label: "Kg",
      value: "KG",
    },
    weightBsaDependentPerWeightBsaUnitName: "Kg",
    weightBsaDependentPerIntervalUnit: {
      label: "day",
      value: "DAYS",
    },
    weightBsaDependentPerIntervalUnitName: "day",
    weightBsaDependentMaxDoseValue: null,
    fixedDoseValue: null,
    fixedDoseUnit: {
      label: "mg",
      value: "MG",
    },
    fixedDoseUnitName: "mg",
    fixedDosePerIntervalUnit: {
      label: "day",
      value: "DAYS",
    },
    fixedDosePerIntervalUnitName: "day",
    maxFixedDoseValue: null,
    maxFixedDoseUnit: {
      label: "mg",
      value: "MG",
    },
    maxFixedDoseUnitName: "mg",
    maxFixedDosePerIntervalUnit: {
      label: "day",
      value: "DAYS",
    },
    maxFixedDosePerIntervalUnitName: "day",
    isDilutionNeeded: false,
    diluentNameId: null,
    diluentName: null,
    diluentValue: null,
    diluentUnit: "ML",
    diluentUnitName: "ml",
    dosePrintUnit: null,
    dosePrintUnitName: null,
    durationOfTreatmentValue: null,
    durationOfTreatmentUnit: {
      label: "Day(s)",
      value: "DAYS",
    },
    durationOfTreatmentUnitName: "Day(s)",
    relationToFoodTimeGapValue: null,
    relationToFoodTimeGapUnit: "MINUTES",
    relationToFoodTimeGapUnitName: "Minutes",
    relationToFoodId: null,
    relationToFoodName: null,
    doseFrequency: null,
    doseFrequencyName: null,
    routeOfAdministrationId: null,
    routeOfAdministrationName: null,
    indicationOfSosMedicineId: null,
    indicationOfSosMedicineName: null,
    isMorningScheduleEnabled: false,
    morningScheduleTime: null,
    isAfternoonScheduleEnabled: false,
    afternoonScheduleTime: null,
    isEveningScheduleEnabled: false,
    eveningScheduleTime: null,
    isNightScheduleEnabled: false,
    nightScheduleTime: null,
    scheduleInstruction: "",
    isDeleted: false,
    medicineDoseInstructionColl: [],
    medicineDoseMedicineColl: Props?.medicineDoseMedicineColl || [],
    medicineGenericId: isForPopUp ? genericNameObject : null,
    medicineGenericName: isForPopUp
      ? genericNameObject?.label?.toUpperCase()
      : "",
    doseDecidingContent: null,
    fileteredMedicineDoseMedicineColl: [],
    previousMedicineDoseId: null,
    nextMedicineDoseId: null,
  };
  // BREAK POINTS
  // const xs = useMediaQuery(theme.breakpoints.up("xs")); //-- FOR REF PLEASE ONLY TAKE THEME BREAK POINTS FOR MEDIA QUERY
  // const sm = useMediaQuery(theme.breakpoints.up("sm"));
  // const md = useMediaQuery(theme.breakpoints.up("md"));
  // const mdu = useMediaQuery(theme.breakpoints.up("md"));
  const mdd = useMediaQuery(theme.breakpoints.down("md"));
  const smd = useMediaQuery(theme.breakpoints.down("sm"));
  const lgd = useMediaQuery(theme.breakpoints.down("lg"));

  // STATES FOR DATA STORAGE  -- PLEASE USE PROPER TYPES FOR STATE
  const [medicineColl, setMedicineColl] = useState<any[]>([]);
  const [medicineUsedAsColl, setMedicineUsedAsColl] = useState<any[]>([]);
  // const [diluentColl, setDiluentColl] = useState<any[]>([]);
  const [relationToFoodColl, setRelationToFoodColl] = useState<any[]>([]);
  const [routeOfAdministrationColl, setRouteOfAdministrationColl] = useState<
    any[]
  >([]);
  const [indicationForSosMedicineColl, setIndicationForSosMedicineColl] =
    useState<any[]>([]);
  const [instructionColl, setInstructionColl] = useState<any[]>([]);
  const [medicineGenericNameColl, setMedicineGenericNameColl] = useState<any[]>(
    [],
  );
  const [strengthUnitColl, setStrengthUnitColl] = useState(
    userContext?.strengthUnitColl || [],
  );
  const [frequencyUnitColl, setFrequencyUnitColl] = useState(
    userContext?.frequencyUnitColl || [],
  );
  const [perIntervalUnitColl, setPerIntervalUnitColl] = useState(
    userContext?.perIntervalUnitColl || [],
  );
  const [treatmentDurationUnitColl, setTreatmentDurationUnitColl] = useState(
    userContext?.treatmentDurationUnitColl || [],
  );

  const [param, setParam] = useState(parameters);

  //INPUT PROPS
  const numberProps = createInputPropsForNumberField(18, 4, true);

  // STATES -- FOR ERROR HANDLING AND LOADING
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [specialityData, setSpecialityData] = useState<any[]>([]);
  const [departmentData, setDepartmentData] = useState<any[]>([]);

  const [customPopUp, setCustomPopUp] = React.useState({
    open: false,
    name: "",
    code: "",
    genericNameObject: null,
    medicineId: 0,
  });

  const handleClickOpen = (
    value: boolean,
    label: string,
    code: string,
    genericNameObject: any,
    medicineId?: number,
  ) => {
    setCustomPopUp({
      ...customPopUp,
      open: value,
      name: label,
      code: code,
      genericNameObject: genericNameObject,
      medicineId: medicineId || 0,
    });
  };

  const handleClose = () => {
    setCustomPopUp({
      open: false,
      name: "",
      code: "",
      genericNameObject: null,
      medicineId: 0,
    });
  };

  const handleManagePopup = async (code, option) => {
    setCustomPopUp({ ...customPopUp, open: false });
    setIsLoading(true);
    if (code === "MEDICINE_GENERIC_CREATE") {
      methods.setValue(
        "medicineGenericId",
        {
          label: option?.medicineGenericLable,
          value: option?.medicineGenericId?.toString(),
        },
        {
          shouldValidate: true,
        },
      );
      await fetchMedicineGenericName("", true);
    } else if (code === "MEDICINE_CREATE") {
      if (option?.MedicineId > 0) {
        const result = await getMedicineDetail(option?.MedicineId);
        if (result) {
          handleMedicineAdd(result);
        }
      }
    } else if (code === "MEDICINE_UPDATE") {
      if (option?.MedicineId > 0) {
        const result = await getMedicineDetail(option?.MedicineId);

        if (result) {
          const detailColl = watchedMedicineDoseMedicineColl || [];

          const updatedColl = detailColl.map((item) => {
            if (item.medicineId === option?.MedicineId) {
              return {
                ...item,
                mode: "D",
                isDeleted: true,
              };
            }
            return item;
          });

          const maxRowNumber = updatedColl.reduce(
            (acc, curr) => (curr.displayOrder > acc ? curr.displayOrder : acc),
            0,
          );

          const newRow: IMedicineDoseMedicine = {
            displayOrder: updatedColl.length === 0 ? 0 : maxRowNumber + 1,
            medicineDoseMedicineId: 0,
            medicineId: Number(result?.value) || 0,
            label: result?.label || "",
            value: result?.value,
            isDeleted: false,
            mode: "I",
            dosePrintUnitName: result?.dosePrintUnitName || "",
            packageTypeName: result?.packageTypeName || "",
            packageAmountOfContent: result?.packageAmountOfContent || 0,
            packageUnitPerVolume: result?.packageUnitPerVolumeName || "",
            doseDecidingContent: result?.doseDecidingContent || "",
            strengthLabel: result?.strengthLabel || "",
            genericName: result?.medicineGenericName || "",
          };

          methods.setValue(
            "medicineDoseMedicineColl",
            [...updatedColl, newRow],
            {
              shouldDirty: true,
              shouldTouch: true,
              shouldValidate: true,
            },
          );
        }
      }
    } else if (code === "MEDICINE_USED_AS_CREATE") {
      methods.setValue("medicineUsedAsId", null, {
        shouldValidate: true,
      });
      methods.setValue("medicineUsedAsName", null, {
        shouldValidate: true,
      });
      await fetchUsedAs("", true);
    } else if (code === "RELATION_TO_FOOD_CREATE") {
      methods.setValue(
        "relationToFoodId",
        {
          label: customPopUp.name,
          value: option?.medicineRelationToFoodId?.toString(),
        },
        {
          shouldValidate: true,
        },
      );
      methods.setValue("relationToFoodName", null, {
        shouldValidate: true,
      });
      await fetchRelationToFood("", true);
    } else if (code === "MEDICINE_ROUTE_CREATE") {
      methods.setValue(
        "routeOfAdministrationId",
        {
          label: customPopUp.name,
          value: option?.medicineRouteOfAdministrationId?.toString(),
        },
        {
          shouldValidate: true,
        },
      );
      methods.setValue("routeOfAdministrationName", null, {
        shouldValidate: true,
      });
      await fetchRouteOfAdministration("", true);
    } else if (code === "INDICATION_FOR_SOS_CREATE") {
      methods.setValue("indicationOfSosMedicineId", null, {
        shouldValidate: true,
      });
      methods.setValue("indicationOfSosMedicineName", null, {
        shouldValidate: true,
      });
      await fetchIndicationForSosMedicine("", true);
    } else if (code === "MEDICINE_DILUENT_CREATE") {
      // methods.setValue("diluentNameId", null, {
      //   shouldValidate: true,
      // });
      // methods.setValue("diluentName", null, {
      //   shouldValidate: true,
      // });
      // await fetchDiluent("", true);
    } else if (code === "INSTUCTION_CREATE") {
      const data = {
        label: option?.instructionData,
        value: option?.instructionId?.toString(),
      };
      handleInstructionAdd(data);
    }
    setIsLoading(false);
  };

  //SCHEMA VALIDATION
  const validationSchema = Yup.object().shape({
    medicineDoseId: Yup.number().nullable(),
    consumerId: Yup.number().required(),
    branchId: Yup.number().required(),
    orgId: Yup.number().required(),
    medicineDoseUnderModule: Yup.object().nullable().shape({
      value: Yup.string(),
      label: Yup.string(),
    }),
    medicineDoseTitle: Yup.string().required(
      "Title is required for Medicine dose",
    ),
    medicineGenericId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
    medicineGenericName: Yup.string().nullable(),
    doseDecidingContent: Yup.string().nullable(),
    // medicineId: Yup.object()
    //   .shape({
    //     value: Yup.string(),
    //     label: Yup.string(),
    //   })
    //   .required("Medicine is required"),
    medicineUsedAsId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
    medicineUsedAsName: Yup.string().nullable(),
    departmentId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable("Department is required"),
    specialityId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable("Speciality is required"),
    weightBsaDependentMinDoseValue: Yup.number()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      )
      .nullable(),
    weightBsaDependentDesiredDoseValue: Yup.number()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      )
      .test(
        "required-if",
        "Desired amount should be between min and max value.",
        (value, ctx) => {
          const minValue = ctx?.from[0].value.weightBsaDependentMinDoseValue;
          const maxValue = ctx?.from[0].value.weightBsaDependentMaxDoseValue;

          // Ensure both min and max are not provided simultaneously
          if (
            ![null, "", undefined, "0"].includes(minValue) &&
            ![null, "", undefined].includes(maxValue)
          ) {
            // Allow the value to be equal to min or max
            if ((value || 0) >= minValue && (value || 0) <= maxValue) {
              return true; // Valid if within the range, inclusive of min and max
            } else {
              return false; // Reject if outside the min/max range
            }
          }

          // If min value is provided
          if (![null, "", undefined].includes(minValue)) {
            if (value && value <= minValue) {
              return false; // Reject if value is less than or equal to the min value
            }
          }

          // If max value is provided
          if (![null, "", undefined].includes(maxValue)) {
            if ((value || 0) >= maxValue) {
              return false; // Reject if value is greater than or equal to the max value
            }
          }

          // If neither min nor max is provided, accept the value
          return true;
        },
      )
      .nullable(),
    weightBsaDependentMaxDoseValue: Yup.number()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      )
      .nullable(),
    weightBsaDependentDesiredDoseUnit: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
    weightBsaDependentDesiredDoseUnitName: Yup.string().nullable(),
    weightBsaDependentPerWeightBsaUnit: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
    weightBsaDependentPerWeightBsaUnitName: Yup.string().nullable(),
    weightBsaDependentPerIntervalUnit: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
    weightBsaDependentPerIntervalUnitName: Yup.string().nullable(),
    fixedDoseValue: Yup.number()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      )
      .nullable(),
    fixedDoseUnit: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
    fixedDoseUnitName: Yup.string().nullable(),
    fixedDosePerIntervalUnit: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
    fixedDosePerIntervalUnitName: Yup.string().nullable(),
    maxFixedDoseValue: Yup.number()
      .nullable()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      ),
    maxFixedDoseUnit: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
    maxFixedDoseUnitName: Yup.string().nullable(),
    maxFixedDosePerIntervalUnit: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
    maxFixedDosePerIntervalUnitName: Yup.string().nullable(),
    isDilutionNeeded: Yup.boolean().required(),
    diluentName: Yup.string().nullable(),
    diluentNameId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .when("isDilutionNeeded", {
        is: true,
        then: (schema) => schema.required("Diluent is required"),
        otherwise: (schema) => schema.nullable(),
      }),
    diluentValue: Yup.number()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      )
      .when("isDilutionNeeded", {
        is: true,
        then: (schema) =>
          schema
            .min(0, "Amount of diluent is required.")
            .required("Amount of diluent is required."),
        otherwise: (schema) => schema.nullable(),
      }),
    diluentUnitName: Yup.string().nullable(),
    diluentUnit: Yup.string().when("isDilutionNeeded", {
      is: true,
      then: (schema) => schema.required("Unit is required."),
      otherwise: (schema) => schema.nullable(),
    }),
    dosePrintUnitName: Yup.string().nullable(),
    dosePrintUnit: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
    durationOfTreatmentValue: Yup.number()
      .nullable()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      )
      .nullable(),
    durationOfTreatmentUnit: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
    durationOfTreatmentUnitName: Yup.string().nullable(),
    relationToFoodTimeGapValue: Yup.number()
      .nullable()

      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      ),
    relationToFoodTimeGapUnit: Yup.string().nullable(),
    relationToFoodTimeGapUnitName: Yup.string().nullable(),
    relationToFoodName: Yup.string().nullable(),
    relationToFoodId: Yup.object().nullable().shape({
      value: Yup.string(),
      label: Yup.string(),
    }),
    doseFrequencyName: Yup.string().nullable(),
    doseFrequency: Yup.object()
      .nullable() // Keep it optional
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .test(
        "too-many-slots",
        "There is discrepency between frequency and medicine schedule please correct before proceeding",
        function () {
          return slotValidationFn(this);
        },
      ),
    routeOfAdministrationName: Yup.string().nullable(),
    routeOfAdministrationId: Yup.object().nullable().shape({
      value: Yup.string(),
      label: Yup.string(),
    }),
    indicationOfSosMedicineName: Yup.string().nullable(),
    indicationOfSosMedicineId: Yup.object().nullable().shape({
      value: Yup.string(),
      label: Yup.string(),
    }),
    scheduleInstruction: Yup.string().nullable(),
    isMorningScheduleEnabled: Yup.boolean().nullable(),
    isAfternoonScheduleEnabled: Yup.boolean().nullable(),
    isEveningScheduleEnabled: Yup.boolean().nullable(),
    isNightScheduleEnabled: Yup.boolean().nullable(),
    medicineDoseInstructionColl: Yup.array()
      .of(
        Yup.object().shape({
          medicineDoseInstructionId: Yup.number().required(),
          displayOrder: Yup.number().required(),
          instructionData: Yup.string().nullable(),
          instructionId: Yup.number().nullable(),
          isDeleted: Yup.boolean().required(),
          mode: Yup.string().required(),
        }),
      )
      .nullable(),
    medicineDoseMedicineColl: Yup.array()
      .of(
        Yup.object().shape({
          medicineDoseMedicineId: Yup.number().required(),
          medicineId: Yup.number().required(),
          displayOrder: Yup.number().required(),
          value: Yup.number().required(),
          label: Yup.string().nullable(),
          isDeleted: Yup.boolean().required(),
          mode: Yup.string().required(),
        }),
      )
      // .min(1, "At least one medicine is required.")
      .test("is-required", "medicine is required.", function (value) {
        if (isForPopUp && isFrom === "MEDICINE") {
          return true;
        }
        return !!value && value.length > 0;
      }),
    fileteredMedicineDoseMedicineColl: Yup.array()
      .of(
        Yup.object().shape({
          medicineDoseMedicineId: Yup.number().required(),
          medicineId: Yup.number().required(),
          displayOrder: Yup.number().required(),
          value: Yup.number().required(),
          label: Yup.string().nullable(),
          isDeleted: Yup.boolean().required(),
          mode: Yup.string().required(),
        }),
      )
      .test("is-required", "medicine is required.", function (value) {
        if (isForPopUp && isFrom === "MEDICINE") {
          return true;
        }
        return !!value && value.length > 0;
      }),
    isDeleted: Yup.boolean(),
  });

  // FORM HOOK
  const methods = useForm({
    resolver: yupResolver(validationSchema), // IGNORE THIS ERROR
    defaultValues: initMedicineDoseData, // DEFAULT VALUE -- DON'T CREATE STATE FOR IT -- STATE CAN INCREASE SITE LOAD
  });

  const watchedMedicineDoseMedicineColl = useWatch({
    name: "medicineDoseMedicineColl",
    control: methods.control,
  });

  const filteredMedicineDoseMedicineColl =
    watchedMedicineDoseMedicineColl?.filter(
      (x) => x?.isDeleted === false && x.mode !== "D",
    ) || [];

  ///HANDLE DELETE
  const handleDeleteMedicineInstruction = (item) => {
    const detailColl = methods.getValues().medicineDoseInstructionColl;

    const updatedColl = detailColl.map((instruction) => {
      if (instruction.displayOrder === item.displayOrder) {
        return { ...instruction, mode: "D", isDeleted: true };
      }
      return instruction; // Return other items unchanged
    });
    methods.setValue("medicineDoseInstructionColl", updatedColl, {
      shouldValidate: true,
    });
  };

  const InstructionCard = ({ item }: { item: IMedicineDoseInstruction }) => {
    return (
      <Box
        sx={{ display: "flex", gap: 2, padding: "10px", alignItems: "center" }}
      >
        <DragIndicatorIcon sx={{ cursor: "grab" }} />
        <Grid container>
          <Grid item xs={12}>
            <Box>{parse(item.instructionData || "")}</Box>
          </Grid>
        </Grid>
        <CloseRoundedIcon
          onClick={() => handleDeleteMedicineInstruction(item)}
          style={{ color: "#d32f2f", cursor: "pointer" }}
        />
      </Box>
    );
  };

  // GET billing DETAIL
  const getMedicineDoseDetail = async () => {
    let item: any;
    let params = new URLSearchParams();
    params.append(
      "medicineDoseId",
      copyMedicineDoseId > 0
        ? copyMedicineDoseId?.toString()
        : medicineDoseId?.toString(),
    );
    params.append("branchId", userContext?.branchId?.toString());
    params.append("orgId", userContext?.organizationId?.toString());
    params.append("consumerId", userContext?.consumerId?.toString());

    try {
      const res: any = await GetMedicineDoseDetail(params);
      if (res?.data?.data) {
        item = res?.data?.data;
        let medicineDoseMedicineColl = item?.medicineDoseMedicineColl || [];

        if (copyMedicineDoseId > 0) {
          medicineDoseMedicineColl = medicineDoseMedicineColl?.map((md) => {
            md.mode = "I";
            md.medicineDoseMedicineId = 0;
            return md;
          });
        }

        methods.reset({
          ...initMedicineDoseData,
          medicineDoseId:
            copyMedicineDoseId > 0 ? 0 : item?.medicineDoseId || 0,
          medicineDoseUnderModule: item?.medicineDoseUnderModule
            ? {
                label: item?.medicineDoseUnderModule,
                value: item?.medicineDoseUnderModule,
              }
            : null,
          medicineDoseTitle: item?.medicineDoseTitle || "",
          // medicineId: item?.medicineId
          //   ? {
          //       label: item?.medicineIdLabel || "",
          //       value: item?.medicineId?.toString(),
          //     }
          //   : null,

          medicineGenericName: item?.medicineGenericName || "",
          medicineGenericId: item?.medicineGenericId
            ? {
                label: item?.medicineGenericName || "",
                value: item?.medicineGenericId?.toString(),
              }
            : null,
          doseDecidingContent: item?.doseDecidingContent || null,
          medicineUsedAsName: item?.medicineUsedAsName || "",
          medicineUsedAsId: item?.medicineUsedAsId
            ? {
                label: item?.medicineUsedAsName || "",
                value: item?.medicineUsedAsId?.toString(),
              }
            : null,
          departmentId: item?.departmentId
            ? {
                label: item?.departmentName,
                value: item?.departmentId,
              }
            : null,
          specialityId: item?.specialityId
            ? {
                label: item?.specialityName,
                value: item?.specialityId,
              }
            : null,
          weightBsaDependentMinDoseValue:
            item?.weightBsaDependentMinDoseValue || null,
          weightBsaDependentDesiredDoseValue:
            item?.weightBsaDependentDesiredDoseValue || null,
          weightBsaDependentDesiredDoseUnit:
            item?.weightBsaDependentDesiredDoseUnit
              ? {
                  label: item?.weightBsaDependentDesiredDoseUnitName || "",
                  value: item?.weightBsaDependentDesiredDoseUnit?.toString(),
                }
              : null,
          weightBsaDependentDesiredDoseUnitName:
            item?.weightBsaDependentDesiredDoseUnitName || "",
          weightBsaDependentPerWeightBsaUnit:
            item?.weightBsaDependentPerWeightBsaUnit
              ? {
                  label: item?.weightBsaDependentPerWeightBsaUnitName || "",
                  value: item?.weightBsaDependentPerWeightBsaUnit?.toString(),
                }
              : null,
          weightBsaDependentPerWeightBsaUnitName:
            item?.weightBsaDependentPerWeightBsaUnitName || "",
          weightBsaDependentPerIntervalUnit:
            item?.weightBsaDependentPerIntervalUnit
              ? {
                  label: item?.weightBsaDependentPerIntervalUnitName || "",
                  value: item?.weightBsaDependentPerIntervalUnit?.toString(),
                }
              : null,
          weightBsaDependentPerIntervalUnitName:
            item?.weightBsaDependentPerIntervalUnitName || "",
          weightBsaDependentMaxDoseValue:
            item?.weightBsaDependentMaxDoseValue || null,
          fixedDoseValue: item?.fixedDoseValue || null,
          fixedDoseUnit: item?.fixedDoseUnit
            ? {
                label: item?.fixedDoseUnitName || "",
                value: item?.fixedDoseUnit?.toString(),
              }
            : null,
          fixedDoseUnitName: item?.fixedDoseUnitName || "",
          fixedDosePerIntervalUnit: item?.fixedDosePerIntervalUnit
            ? {
                label: item?.fixedDosePerIntervalUnitName || "",
                value: item?.fixedDosePerIntervalUnit?.toString(),
              }
            : null,
          fixedDosePerIntervalUnitName:
            item?.fixedDosePerIntervalUnitName || "",
          maxFixedDoseValue: item?.maxFixedDoseValue || null,
          maxFixedDoseUnit: item?.maxFixedDoseUnit
            ? {
                label: item?.maxFixedDoseUnitName || "",
                value: item?.maxFixedDoseUnit?.toString(),
              }
            : null,
          maxFixedDoseUnitName: item?.maxFixedDoseUnitName || "",
          maxFixedDosePerIntervalUnit: item?.maxFixedDosePerIntervalUnit
            ? {
                label: item?.maxFixedDosePerIntervalUnitName || "",
                value: item?.maxFixedDosePerIntervalUnit?.toString(),
              }
            : null,
          maxFixedDosePerIntervalUnitName:
            item?.maxFixedDosePerIntervalUnitName || "",
          isDilutionNeeded: item?.isDilutionNeeded || 0,
          diluentName: item?.diluentName || "",
          diluentNameId: item?.diluentNameId
            ? {
                label: item?.diluentName || "",
                value: item?.diluentNameId?.toString(),
              }
            : null,
          diluentValue: item?.diluentValue || 0,
          diluentUnit: item?.diluentUnit || 0,
          diluentUnitName: item?.diluentUnitName || "",
          dosePrintUnitName: item?.dosePrintUnitName || "",
          dosePrintUnit: item?.dosePrintUnit
            ? {
                label: item?.dosePrintUnitName?.toString() || "",
                value: item?.dosePrintUnit?.toString(),
              }
            : null,
          durationOfTreatmentValue: item?.durationOfTreatmentValue || 0,
          durationOfTreatmentUnit: item?.durationOfTreatmentUnit
            ? {
                label: item?.durationOfTreatmentUnitName?.toString() || "",
                value: item?.durationOfTreatmentUnit?.toString(),
              }
            : null,
          durationOfTreatmentUnitName: item?.durationOfTreatmentUnitName || "",
          relationToFoodTimeGapValue: item?.relationToFoodTimeGapValue || 0,
          relationToFoodTimeGapUnit: item?.relationToFoodTimeGapUnit || "",
          relationToFoodTimeGapUnitName:
            item?.relationToFoodTimeGapUnitName || "",
          relationToFoodName: item?.relationToFoodName || "",
          relationToFoodId: item?.relationToFoodId
            ? {
                label: item?.relationToFoodName || "",
                value: item?.relationToFoodId?.toString(),
              }
            : null,
          doseFrequencyName: item?.doseFrequencyName || "",
          doseFrequency: item?.doseFrequency
            ? {
                label: item?.doseFrequencyName || "",
                value: item?.doseFrequency?.toString(),
                frequencyConversationValue: item.frequencyConversationValue,
                frequencyConversationUnit: item.frequencyConversationUnit,
              }
            : null,
          routeOfAdministrationName: item?.routeOfAdministrationName || "",
          routeOfAdministrationId: item?.routeOfAdministrationId
            ? {
                label: item?.routeOfAdministrationName || "",
                value: item?.routeOfAdministrationId?.toString(),
              }
            : null,
          indicationOfSosMedicineName: item?.indicationOfSosMedicineName || "",
          indicationOfSosMedicineId: item?.indicationOfSosMedicineId
            ? {
                label: item?.indicationOfSosMedicineName || "",
                value: item?.indicationOfSosMedicineId?.toString(),
              }
            : null,
          isMorningScheduleEnabled: item?.isMorningScheduleEnabled || false,
          morningScheduleTime: item?.morningScheduleTime
            ? dayjs(item?.morningScheduleTime)
            : null,
          isAfternoonScheduleEnabled: item?.isAfternoonScheduleEnabled || false,
          afternoonScheduleTime: item?.afternoonScheduleTime
            ? dayjs(item?.afternoonScheduleTime)
            : null,
          isEveningScheduleEnabled: item?.isEveningScheduleEnabled || false,
          eveningScheduleTime: item?.eveningScheduleTime
            ? dayjs(item?.eveningScheduleTime)
            : null,
          isNightScheduleEnabled: item?.isNightScheduleEnabled || false,
          nightScheduleTime: item?.nightScheduleTime
            ? dayjs(item?.nightScheduleTime)
            : null,
          scheduleInstruction: item?.scheduleInstruction || "",
          medicineDoseInstructionColl: item?.medicineDoseInstructionColl || [],
          medicineDoseMedicineColl: item?.medicineDoseMedicineColl || [],
          fileteredMedicineDoseMedicineColl: medicineDoseMedicineColl || [],
          previousMedicineDoseId: item?.previousMedicineDoseId || 0,
          nextMedicineDoseId: item?.nextMedicineDoseId || 0,
        });
      }
      return item;
    } catch (e) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // SAVE AND UPDATE BRANCH
  const saveMedicineDose = async (value: formInitialValueTypes) => {
    try {
      let result: any = "";
      let formData = {
        medicineDoseId: value.medicineDoseId,
        consumerId: userContext?.consumerId,
        orgId: userContext?.organizationId,
        branchId: userContext?.branchId,
        medicineDoseUnderModule: value?.medicineDoseUnderModule?.value,
        medicineGenericId: value?.medicineGenericId?.value,
        medicineDoseTitle: value?.medicineDoseTitle?.trim() || "",
        medicineGenericName: value?.medicineGenericName?.trim() || "",
        doseDecidingContent: value?.doseDecidingContent?.trim() || "",
        // medicineId: value?.medicineId?.value,
        medicineUsedAsName: value?.medicineUsedAsName?.trim() || "",
        medicineUsedAsId: value?.medicineUsedAsId?.value,
        specialityId: value.specialityId?.value || 0,
        departmentId: value.departmentId?.value || 0,
        weightBsaDependentMinDoseValue:
          value?.weightBsaDependentMinDoseValue || null,
        weightBsaDependentDesiredDoseValue:
          value?.weightBsaDependentDesiredDoseValue || null,
        weightBsaDependentDesiredDoseUnit:
          value?.weightBsaDependentDesiredDoseUnit?.value || "",
        weightBsaDependentDesiredDoseUnitName:
          value?.weightBsaDependentDesiredDoseUnitName || "",
        weightBsaDependentPerWeightBsaUnit:
          value?.weightBsaDependentPerWeightBsaUnit?.value || "",
        weightBsaDependentPerWeightBsaUnitName:
          value?.weightBsaDependentPerWeightBsaUnitName || "",
        weightBsaDependentPerIntervalUnit:
          value?.weightBsaDependentPerIntervalUnit?.value || "",
        weightBsaDependentPerIntervalUnitName:
          value?.weightBsaDependentPerIntervalUnitName || "",
        weightBsaDependentMaxDoseValue:
          value?.weightBsaDependentMaxDoseValue || null,
        fixedDoseValue: value?.fixedDoseValue || null,
        fixedDoseUnit: value?.fixedDoseUnit?.value || "",
        fixedDoseUnitName: value?.fixedDoseUnitName || "",
        fixedDosePerIntervalUnit: value?.fixedDosePerIntervalUnit?.value || "",
        fixedDosePerIntervalUnitName: value?.fixedDosePerIntervalUnitName || "",
        maxFixedDoseValue: value?.maxFixedDoseValue || null,
        maxFixedDoseUnit: value?.maxFixedDoseUnit?.value || "",
        maxFixedDoseUnitName: value?.maxFixedDoseUnitName || "",
        maxFixedDosePerIntervalUnit:
          value?.maxFixedDosePerIntervalUnit?.value || "",
        maxFixedDosePerIntervalUnitName:
          value?.maxFixedDosePerIntervalUnitName || "",
        isDilutionNeeded: value?.isDilutionNeeded || false,
        diluentNameId: value?.diluentNameId?.value,
        diluentName: value?.diluentName || "",
        diluentValue: value?.diluentValue || 0,
        diluentUnit: value?.diluentUnit,
        diluentUnitName: value?.diluentUnitName || "",
        dosePrintUnit: value?.dosePrintUnit?.value,
        dosePrintUnitName: value?.dosePrintUnitName || "",
        durationOfTreatmentValue: value?.durationOfTreatmentValue || 0,
        durationOfTreatmentUnit: value?.durationOfTreatmentUnit?.value || "",
        durationOfTreatmentUnitName: value?.durationOfTreatmentUnitName || "",
        relationToFoodTimeGapValue: value?.relationToFoodTimeGapValue || 0,
        relationToFoodTimeGapUnit: value?.relationToFoodTimeGapUnit || "",
        relationToFoodTimeGapUnitName:
          value?.relationToFoodTimeGapUnitName || "",
        relationToFoodId: value?.relationToFoodId?.value,
        relationToFoodName: value?.relationToFoodName || "",
        doseFrequency: value?.doseFrequency?.value,
        doseFrequencyName: value?.doseFrequencyName || "",
        routeOfAdministrationId: value?.routeOfAdministrationId?.value,
        routeOfAdministrationName: value?.routeOfAdministrationName || "",
        indicationOfSosMedicineName: value?.indicationOfSosMedicineName || "",
        indicationOfSosMedicineId: value?.indicationOfSosMedicineId?.value,
        isMorningScheduleEnabled: value?.isMorningScheduleEnabled || false,
        morningSheduledTimeStr: value?.morningScheduleTime
          ? new Date(value?.morningScheduleTime).toLocaleTimeString()
          : null,
        isAfternoonScheduleEnabled: value?.isAfternoonScheduleEnabled || false,
        afternoonSheduledTimeStr: value?.afternoonScheduleTime
          ? new Date(value?.afternoonScheduleTime).toLocaleTimeString()
          : null,
        isEveningScheduleEnabled: value?.isEveningScheduleEnabled || false,
        eveningSheduledTimeStr: value?.eveningScheduleTime
          ? new Date(value?.eveningScheduleTime).toLocaleTimeString()
          : null,
        isNightScheduleEnabled: value?.isNightScheduleEnabled || false,
        nightSheduledTimeStr: value?.nightScheduleTime
          ? new Date(value?.nightScheduleTime).toLocaleTimeString()
          : null,
        scheduleInstruction: value?.scheduleInstruction?.trim() || "",
        medicineDoseMedicineCollJson: JSON.stringify(
          value?.medicineDoseMedicineColl || "",
        ),
        medicineDoseInstructionCollJson: JSON.stringify(
          value?.medicineDoseInstructionColl || "",
        ),
        isDeleted: value.isDeleted,
        dataManagedBy: userContext.loginDetail.userId,
      };
      if (medicineDoseId > 0) {
        result = await UpdateMedicineDose(formData);
      } else {
        result = await CreateMedicineDose(formData);
      }

      if (result?.data) {
        if (!isForPopUp) {
          if (param.saveType === "SAVE_&_COPY") {
            handleCopyMedicineDose(result?.data?.medicineDoseId || 0);
          } else if (
            ["SAVE_&_NEXT", "SAVE_&_PREVIOUS"].includes(param.saveType)
          ) {
            handlePreviousOrNExtMedicineDose(
              param.saveType === "SAVE_&_NEXT"
                ? value?.nextMedicineDoseId
                : value?.previousMedicineDoseId,
            );
          } else {
            navigate(-1);
          }
        } else {
          if (result?.data) {
            handleClosePopUp("MEDICINE_DOSE_CREATE", result?.data);
          }
        }
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: result?.message,
          alertType: AlertType.Success,
        });
      } else {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: "Something went wrong.",
          alertType: AlertType.Error,
        });
      }
    } catch (e) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // ON SUBMIT OF FORM
  const onSubmit = async (data: any) => {
    saveMedicineDose(data);
  };

  // HANDLE BACK
  const handleBack = () => {
    if (isForPopUp) {
      handleClosePopUp("CLOSE");
    } else {
      navigate(-1);
      // navigate("/master/medicine-dose?page=1&q=");
    }
  };

  // -- GENERIC NAME ----//

  // GET GENERIC NAME
  async function fetchMedicineGenericName(
    searchValue: string,
    oninit: boolean,
  ) {
    try {
      let medicineGenericNameColl = [];
      let params = new URLSearchParams();
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("orgId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", searchValue.toString());
      params.append("mode", "DROPDOWN");
      const hsnSacData: any = await GetMedicineGenericNameList(params);
      if (hsnSacData?.data?.data?.medicineGenericNameColl) {
        medicineGenericNameColl = hsnSacData.data.data.medicineGenericNameColl;
        if (oninit) setMedicineGenericNameColl(medicineGenericNameColl);
      }
      return medicineGenericNameColl;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  // FILTER GENERIC NAME
  async function filterMedicineGenericName(inputValue: string) {
    let result = await fetchMedicineGenericName(inputValue, false);
    return result;
  }

  // GENERIC NAME LOAD ON SEARCH
  const loadMedicineGenericNameList = (
    inputValue: string,
    callback: (options: any) => void,
  ) => {
    if (inputValue && inputValue.length > 0) {
      filterMedicineGenericName(inputValue?.trim()).then((r) => {
        callback(r);
      });
    } else {
      callback([]);
    }
  };

  // --- MEDICINE ---- //

  // GET MEDICINE
  async function fetchMedicine(
    genericNameId: string,
    searchValue: string,
    oninit: boolean,
  ) {
    try {
      let medicineColl = [];
      let params = new URLSearchParams();
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("orgId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      if (genericNameId) {
        params.append("genericNameId", genericNameId);
      }
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", searchValue);
      params.append("mode", "DROPDOWN");
      const medicineData: any = await GetMedicineList(params);
      if (medicineData?.data?.data?.medicineColl) {
        medicineColl = medicineData.data.data.medicineColl;
        if (oninit) setMedicineColl(medicineColl);
      }
      return medicineColl;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  // FILTER MEDICINE
  async function filterMedicine(genericNameId: string, inputValue: string) {
    let result = await fetchMedicine(genericNameId, inputValue, false);
    return result;
  }

  // MEDICINE LOAD ON SEARCH
  const loadMedicineList = (
    genericNameId: string,
    inputValue: string,
    callback: (options: any) => void,
  ) => {
    if (inputValue && inputValue.length > 0) {
      filterMedicine(genericNameId, inputValue).then((r) => {
        callback(r);
      });
    } else {
      callback([]);
    }
  };

  const handleDeleteMedicine = (
    mode: string,
    medicine?: IMedicineDoseMedicine,
  ) => {
    if (mode === "ALL") {
      let detailColl =
        watchedMedicineDoseMedicineColl?.map((item) => {
          item.isDeleted = true;
          item.mode = "D";
          return item;
        }) || [];

      methods.setValue("medicineDoseMedicineColl", detailColl);
      methods.setValue("fileteredMedicineDoseMedicineColl", []);
    } else if (mode === "ROW" && medicine) {
      let detailColl = watchedMedicineDoseMedicineColl || [];
      detailColl[medicine.displayOrder].isDeleted = true;
      detailColl[medicine.displayOrder].mode = "D";

      methods.setValue("medicineDoseMedicineColl", detailColl);
      let filteredDetailColl =
        detailColl?.filter((x) => x?.isDeleted === false && x.mode !== "D") ||
        [];
      methods.setValue("fileteredMedicineDoseMedicineColl", filteredDetailColl);
    }
  };

  const handleMedicineAdd = (value: any) => {
    const detailColl = watchedMedicineDoseMedicineColl || [];
    const maxRowNumber = detailColl?.reduce(
      (acc, curr) => (acc = curr.displayOrder > acc ? curr.displayOrder : acc),
      0,
    );

    const newRow: IMedicineDoseMedicine = {
      displayOrder: detailColl.length === 0 ? 0 : maxRowNumber + 1,
      medicineDoseMedicineId: 0,
      medicineId: value?.value || 0,
      label: value?.label || "",
      value: value?.value,
      isDeleted: false,
      mode: "I",
      dosePrintUnitName: value?.dosePrintUnitName || "",
      packageTypeName: value?.packageTypeName || "",
      packageAmountOfContent: value?.packageAmountOfContent || 0,
      packageUnitPerVolume: value?.packageUnitPerVolumeName || "",
      doseDecidingContent: value?.doseDecidingContent || "",
      strengthLabel: value?.strengthLabel || "",
      genericName: value?.medicineGenericName || "",
    };
    methods.setValue("medicineDoseMedicineColl", [...detailColl, newRow], {
      shouldValidate: true,
    });

    methods.setValue(
      "fileteredMedicineDoseMedicineColl",
      [...filteredMedicineDoseMedicineColl, newRow],
      {
        shouldValidate: true,
      },
    );
  };

  const handleAddAllMedicine = (options) => {
    const detailColl = watchedMedicineDoseMedicineColl || [];

    const maxRowNumber = detailColl?.reduce(
      (acc, curr) => (acc = curr.displayOrder > acc ? curr.displayOrder : acc),
      0,
    );

    const existingMedicineDoseIds = new Set(
      detailColl.map((item) => item.value),
    );

    let newDetailColl = options?.reduce((acc, curr, index) => {
      if (!curr?.__isNew__ && !existingMedicineDoseIds.has(curr?.value)) {
        const newRow: IMedicineDoseMedicine = {
          displayOrder:
            detailColl.length === 0 ? index : index + maxRowNumber + 1,
          medicineDoseMedicineId: 0,
          medicineId: curr?.value || 0,
          label: curr?.label || "",
          value: curr?.value,
          isDeleted: false,
          mode: "I",
          dosePrintUnitName: curr?.dosePrintUnitName || "",
          packageTypeName: curr?.packageTypeName || "",
          packageAmountOfContent: curr?.packageAmountOfContent || null,
          packageUnitPerVolume: curr?.packageUnitPerVolumeName || null,
          doseDecidingContent: curr?.doseDecidingContent || null,
        };
        acc = [...acc, newRow];
      }
      return acc;
    }, []);

    methods.setValue(
      "medicineDoseMedicineColl",
      [...detailColl, ...newDetailColl],
      {
        shouldValidate: true,
      },
    );

    methods.setValue("fileteredMedicineDoseMedicineColl", [
      ...filteredMedicineDoseMedicineColl,
      ...newDetailColl,
    ]);
  };

  const medicineOptionComponent = (props) => {
    return (
      <components.Option {...props}>
        {props.data.__isNew__ && <Box>{props.children}</Box>}
        {props.data && !props.data.__isNew__ && (
          <Box>
            <Typography>{props?.children}</Typography>
          </Box>
        )}
      </components.Option>
    );
  };

  const medicineMenuListComponent = (props) => {
    let optionsLength = 0;
    if (Array.isArray(props?.children)) {
      optionsLength =
        props?.children?.filter((item) => !item?.props?.data?.__isNew__)
          ?.length || 0;
    }
    return (
      <components.MenuList {...props}>
        {optionsLength > 0 && (
          <Box
            sx={{
              px: 2,
              py: 1,
            }}
          >
            <Tooltip
              title={"This will add all searched options"}
              placement="right"
            >
              <Typography
                sx={{ width: "max-content" }}
                className="text-link link-cursor"
                onClick={() => {
                  if (props?.options?.length > 0) {
                    handleAddAllMedicine(props.options);
                  }
                }}
              >
                ALL
              </Typography>
            </Tooltip>
          </Box>
        )}

        {props.children}
      </components.MenuList>
    );
  };

  const medicineInstructionOptionComponent = (props: any) => {
    return (
      <components.Option {...props}>
        {props.data.__isNew__ && <Box>{props.children}</Box>}
        {props.data && !props.data.__isNew__ && (
          <Box
            dangerouslySetInnerHTML={{
              __html: props.children,
            }}
          />
        )}
      </components.Option>
    );
  };

  const MedicineCard = ({ item }: { item: IMedicineDoseMedicine }) => {
    return (
      <Box
        sx={{ display: "flex", gap: 2, padding: "10px", alignItems: "center" }}
      >
        <Grid container>
          <Grid item xs={12} lg={4}>
            <Typography
              className="text-link link-cursor"
              onClick={() => {
                handleClickOpen(
                  true,
                  item?.label,
                  "MEDICINE",
                  null,
                  item?.value,
                );
              }}
            >
              {item?.label || ""}
            </Typography>
            <Box
              sx={{
                display: lgd ? "block" : "none",
              }}
            >
              <Grid container spacing={0.5}>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography>
                    <span className="bold-600">
                      Strength of dose deciding Content:
                    </span>{" "}
                    {item?.strengthLabel || " - "}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography>
                    <span className="bold-600">Dose deciding content:</span>{" "}
                    {item?.genericName || " - "}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography>
                    <span className="bold-600">Print Unit:</span>{" "}
                    {item?.dosePrintUnitName || " - "}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography>
                    <span className="bold-600">Package:</span>{" "}
                    {item?.packageAmountOfContent ||
                    item?.packageUnitPerVolume ||
                    item?.packageTypeName
                      ? item?.packageAmountOfContent +
                        " " +
                        item?.packageUnitPerVolume +
                        " " +
                        item?.packageTypeName
                      : " - "}{" "}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Grid>
          <Grid
            item
            lg={2}
            display={{
              xs: "none",
              lg: "grid",
            }}
          >
            <Typography>{item?.strengthLabel || ""}</Typography>
          </Grid>
          <Grid
            item
            lg={3}
            display={{
              xs: "none",
              lg: "grid",
            }}
          >
            <Typography>{item?.genericName || ""}</Typography>
          </Grid>
          <Grid
            item
            lg={1.5}
            display={{
              xs: "none",
              lg: "grid",
            }}
          >
            <Typography>{item?.dosePrintUnitName || ""}</Typography>
          </Grid>
          <Grid
            item
            lg={1.5}
            display={{
              xs: "none",
              lg: "grid",
            }}
          >
            <Typography>
              {item.packageAmountOfContent} {item.packageUnitPerVolume}{" "}
              {item.packageTypeName}
            </Typography>
          </Grid>
        </Grid>
        <CloseRoundedIcon
          onClick={() => {
            handleDeleteMedicine("ROW", item);
          }}
          style={{ color: "#d32f2f", cursor: "pointer" }}
        />
      </Box>
    );
  };

  // --- USED AS ---- //

  // GET USED AS
  async function fetchUsedAs(searchValue: string, oninit: boolean) {
    try {
      let medicineUsedAsColl = [];
      let params = new URLSearchParams();
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("orgId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", searchValue);
      params.append("mode", "DROPDOWN");
      const usedAsData: any = await GetMedicineUsedAsList(params);
      if (usedAsData?.data?.data?.medicineUsedAsColl) {
        medicineUsedAsColl = usedAsData.data.data.medicineUsedAsColl;
        if (oninit) setMedicineUsedAsColl(medicineUsedAsColl);
      }
      return medicineUsedAsColl;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  // FILTER USED AS
  async function filterUsedAs(inputValue: string) {
    let result = await fetchUsedAs(inputValue, false);
    return result;
  }

  // USED AS LOAD ON SEARCH
  const loadUsedAsList = (
    inputValue: string,
    callback: (options: any) => void,
  ) => {
    if (inputValue && inputValue.length > 0) {
      filterUsedAs(inputValue?.trim()).then((r) => {
        callback(r);
      });
    } else {
      callback([]);
    }
  };

  // GET RELATION TO FOOD
  async function fetchRelationToFood(searchValue: string, oninit: boolean) {
    try {
      let medicineRelationToFoodColl = [];
      let params = new URLSearchParams();
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("orgId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", searchValue);
      params.append("mode", "DROPDOWN");
      params.append(
        "medicineRelationToFoodTypeCode",
        param.medicineRelationToFoodTypeCode,
      );
      const usedAsData: any = await GetMedicineRelationToFoodList(params);
      if (usedAsData?.data?.data?.medicineRelationToFoodColl) {
        medicineRelationToFoodColl =
          usedAsData.data.data.medicineRelationToFoodColl;
        if (oninit) setRelationToFoodColl(medicineRelationToFoodColl);
      }
      return medicineRelationToFoodColl;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  // FILTER RELATION TO FOOD
  async function filterRelationToFood(inputValue: string) {
    let result = await fetchRelationToFood(inputValue, false);
    return result;
  }

  // RELATION TO FOOD LOAD ON SEARCH
  const loadRelationToFoodList = (
    inputValue: string,
    callback: (options: any) => void,
  ) => {
    if (inputValue && inputValue.length > 0) {
      filterRelationToFood(inputValue?.trim()).then((r) => {
        callback(r);
      });
    } else {
      callback([]);
    }
  };

  // GET ROUTE OF ADMINISTRATION
  async function fetchRouteOfAdministration(
    searchValue: string,
    oninit: boolean,
  ) {
    try {
      let medicineRouteOfAdministrationColl = [];
      let params = new URLSearchParams();
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("orgId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", searchValue);
      params.append("mode", "DROPDOWN");
      const usedAsData: any =
        await GetMedicineRouteOfAdministrationList(params);
      if (usedAsData?.data?.data?.medicineRouteOfAdministrationColl) {
        medicineRouteOfAdministrationColl =
          usedAsData.data.data.medicineRouteOfAdministrationColl;
        if (oninit)
          setRouteOfAdministrationColl(medicineRouteOfAdministrationColl);
      }
      return medicineRouteOfAdministrationColl;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  // FILTER ROUTE OF ADMINISTRATION
  async function filterRouteOfAdministration(inputValue: string) {
    let result = await fetchRouteOfAdministration(inputValue, false);
    return result;
  }

  // ROUTE OF ADMINISTRATION LOAD ON SEARCH
  const loadRouteOfAdministrationList = (
    inputValue: string,
    callback: (options: any) => void,
  ) => {
    if (inputValue && inputValue.length > 0) {
      filterRouteOfAdministration(inputValue?.trim()).then((r) => {
        callback(r);
      });
    } else {
      callback([]);
    }
  };

  // --- INDICATION FOR SOS MEDICINE ---- //

  // GET INDICATION FOR SOS MEDICINE
  async function fetchIndicationForSosMedicine(
    searchValue: string,
    oninit: boolean,
  ) {
    try {
      let medicineIndicationForSOSMedicineColl = [];
      let params = new URLSearchParams();
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("orgId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", searchValue);
      params.append("mode", "DROPDOWN");
      // params.append("medicineIndicationForSOSTypeCode", "Medicine_Indication_For_SOS_Medicine");

      const usedAsData: any =
        await GetMedicineIndicationForSOSMedicineList(params);
      if (usedAsData?.data?.data?.medicineIndicationForSOSMedicineColl) {
        medicineIndicationForSOSMedicineColl =
          usedAsData.data.data.medicineIndicationForSOSMedicineColl;
        if (oninit)
          setIndicationForSosMedicineColl(medicineIndicationForSOSMedicineColl);
      }
      return medicineIndicationForSOSMedicineColl;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  // FILTER INDICATION FOR SOS MEDICINE
  async function filterIndicationForSosMedicine(inputValue: string) {
    let result = await fetchIndicationForSosMedicine(inputValue, false);
    return result;
  }

  // INDICATION FOR SOS MEDICINE LOAD ON SEARCH
  const loadIndicationForSosMedicineList = (
    inputValue: string,
    callback: (options: any) => void,
  ) => {
    if (inputValue && inputValue.length > 0) {
      filterIndicationForSosMedicine(inputValue?.trim()).then((r) => {
        callback(r);
      });
    } else {
      callback([]);
    }
  };

  // --- MEDICINE INSTRUCTION ---- //

  // GET MEDICINE INSTRUCTION
  async function fetchMedicineInstruction(
    searchValue: string,
    oninit: boolean,
  ) {
    try {
      let instructionColl = [];
      let params = new URLSearchParams();
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("orgId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", searchValue);
      params.append("instructionTypeCode", "MEDICINE_INSTRUCTION");
      params.append("mode", "DROPDOWN");
      const usedAsData: any = await GetInstructionList(params);
      if (usedAsData?.data?.data?.instructionColl) {
        instructionColl = usedAsData.data.data.instructionColl;
        if (oninit) setInstructionColl(instructionColl);
      }
      return instructionColl;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  // FILTER MEDICINE INSTRUCTION
  async function filterMedicineInstruction(inputValue: string) {
    let result = await fetchMedicineInstruction(inputValue, false);
    return result;
  }

  // MEDICINE INSTRUCTION LOAD ON SEARCH
  const loadMedicineInstructionList = (
    inputValue: string,
    callback: (options: any) => void,
  ) => {
    if (inputValue && inputValue.length > 0) {
      filterMedicineInstruction(inputValue).then((r) => {
        callback(r);
      });
    } else {
      callback([]);
    }
  };

  //HANDLE INSTRUCTION CVALUE CHANGE
  const handleInstructionAdd = (value: any) => {
    const detailColl = methods?.getValues().medicineDoseInstructionColl;

    const maxRowNumber = detailColl.reduce(
      (acc, curr) => (acc = curr.displayOrder > acc ? curr.displayOrder : acc),
      0,
    );

    const newRow: IMedicineDoseInstruction = {
      displayOrder: maxRowNumber + 1,
      medicineDoseInstructionId: 0,
      instructionData: value?.label || "",
      instructionId: value?.value || 0,
      isDeleted: false,
      mode: "I",
    };

    methods.reset(
      {
        ...methods.getValues(),
        medicineDoseInstructionColl: [...detailColl, newRow],
      },
      {
        keepErrors: true,
      },
    );
  };

  const handleMedicineInstructionOrderChange = (value: any) => {
    const { dragIndex, hoverIndex } = value;
    const medicineDoseInstructionColl =
      methods.getValues().medicineDoseInstructionColl;

    // Clone the array to avoid direct mutation
    const updatedItems = [...medicineDoseInstructionColl];

    // Get the item at dragIndex
    const draggedItem = updatedItems[dragIndex];

    // Remove the item from its original position (dragIndex)
    updatedItems.splice(dragIndex, 1);

    // Insert the item at hoverIndex
    updatedItems.splice(hoverIndex, 0, draggedItem);

    // Update the displayOrder of each item in the updated array
    const updatedItemsWithDisplayOrder = updatedItems.map((item, index) => {
      return {
        ...item,
        displayOrder: index + 1, // Adjust this if needed to fit your use case
      };
    });

    methods.setValue(
      "medicineDoseInstructionColl",
      updatedItemsWithDisplayOrder,
      {
        shouldValidate: true,
      },
    );
  };

  // HANDLE PREVIOUS OR NEXT MEDICINE DOSE
  const handlePreviousOrNExtMedicineDose = (medicineId: any) => {
    let encryptmedicinetCopyId: any = edService.encrypt(medicineId);
    navigate(
      {
        pathname: "/master/medicine-dose/medicine-dose-save",
        search: `?q=${encodeURIComponent(encryptmedicinetCopyId)}`,
      },
      { replace: true },
    );
  };

  // GET  STRENGTH UNITS
  async function fetchStrengthUnitList() {
    try {
      let strengthUnitColl = [];
      const result: any = await GetStrengthUnitList();
      if (result?.data?.data) {
        strengthUnitColl = result?.data?.data;
        updateUserContext({
          ...userContext,
          strengthUnitColl: strengthUnitColl,
        });
        setStrengthUnitColl(strengthUnitColl);
      }
      return strengthUnitColl;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  // GET  FREQUENCY UNITS
  async function fetchFrequencyUnitList() {
    try {
      let frequencyUnitColl = [];
      const result: any = await GetFrequencyUnitList();
      if (result?.data?.data) {
        frequencyUnitColl = result?.data?.data;
        updateUserContext({
          ...userContext,
          frequencyUnitColl: frequencyUnitColl,
        });
        setFrequencyUnitColl(frequencyUnitColl);
      }
      return frequencyUnitColl;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  // GET PER INTERVAL UNITS
  async function fetchPerIntervalUnitList() {
    try {
      let perIntervalUnitColl = [];
      const result: any = await GetPerIntervalUnitList();
      if (result?.data?.data) {
        perIntervalUnitColl = result?.data?.data;
        updateUserContext({
          ...userContext,
          perIntervalUnitColl: perIntervalUnitColl,
        });
        setPerIntervalUnitColl(perIntervalUnitColl);
      }
      return perIntervalUnitColl;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  // GET TREATMENT DURATION UNITS
  async function fetchTreatmentDurationUnitList() {
    try {
      let treatmentDurationUnitColl = [];
      const result: any = await GetTreatmentDurationUnitList();
      if (result?.data?.data) {
        treatmentDurationUnitColl = result?.data?.data;
        updateUserContext({
          ...userContext,
          treatmentDurationUnitColl: treatmentDurationUnitColl,
        });
        setTreatmentDurationUnitColl(treatmentDurationUnitColl);
      }
      return treatmentDurationUnitColl;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  // HANDLE COPPPY MEDICINE DOSE
  const handleCopyMedicineDose = (medicineDoseId: any) => {
    let encryptmedicinetCopyId: any = edService.encrypt(medicineDoseId);
    let encryptmedicinetId: any = edService.encrypt(0);
    navigate(
      {
        pathname: "/master/medicine-dose/medicine-dose-save",
        search: `?q=${encodeURIComponent(
          encryptmedicinetId,
        )}&&c=${encodeURIComponent(encryptmedicinetCopyId)}`,
      },
      {
        replace: true,
        state: {
          title: "Copy",
          button: "Save",
          medicineDoseId: medicineDoseId,
        },
      },
    );
  };

  //DEPARTMENT LIST
  const getDepartmentList = async () => {
    try {
      let params = new URLSearchParams();
      params.append("ConsumerId", userContext?.consumerId.toString());
      params.append("OrgId", userContext?.organizationId.toString());
      params.append("BranchId", userContext?.branchId.toString());
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", param.searchValue);
      const result: any = await GetDepartmentList(params);
      if (result?.data?.data?.departmentColl) {
        const resultData = result?.data?.data.departmentColl;
        setDepartmentData(resultData);
      }
      setIsLoading(false);
    } catch (e: any) {
      setIsLoading(false);
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  //Speciality LIST
  async function fetchSpeciality(departmentIds: string) {
    try {
      let params = new URLSearchParams();
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("orgId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("departmentIds", departmentIds);
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", param.searchValue);
      params.append("mode", "DROPDOWN".toString());
      const specialityData: any = await GetSpecialityList(params);
      if (specialityData?.data?.data?.specialityColl) {
        let specialityCollection = specialityData.data.data.specialityColl;
        setSpecialityData(specialityCollection);
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  // GET MEDICINE DETAIL
  const getMedicineDetail = async (medicineId: number) => {
    let item: any;
    let params = new URLSearchParams();
    params.append("medicineId", medicineId?.toString());
    params.append("branchId", userContext?.branchId?.toString());
    params.append("orgId", userContext?.organizationId?.toString());
    params.append("consumerId", userContext?.consumerId?.toString());

    try {
      const res: any = await GetMedicineDetail(params);
      item = res.data.data;
      return item;
    } catch (e) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // GET Medicine Diluent Name DETAIL
  const getInstructionDetailById = async (instructionId: number) => {
    let Instruction: any;
    let params = new URLSearchParams();
    params.append("instructionId", instructionId?.toString());
    params.append("ConsumerId", userContext?.consumerId?.toString());
    params.append("OrgId", userContext?.organizationId?.toString());
    params.append("branchId", userContext?.branchId?.toString());
    try {
      const res: any = await GetInstructionDetail(params);
      Instruction = res.data.data;

      return Instruction;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // FETCH DATA
  const fetchData = async () => {
    try {
      await fetchMedicineGenericName("", true);
      await getDepartmentList();
      await fetchUsedAs("", true);
      await fetchSpeciality("", true);
      // await fetchDiluent("", true);
      await fetchRelationToFood("", true);
      await fetchRouteOfAdministration("", true);
      await fetchIndicationForSosMedicine("", true);
      await fetchMedicineInstruction("", true);
      if (strengthUnitColl?.length <= 0) {
        await fetchStrengthUnitList();
      }
      if (frequencyUnitColl?.length <= 0) {
        await fetchFrequencyUnitList();
      }
      if (perIntervalUnitColl?.length <= 0) {
        await fetchPerIntervalUnitList();
      }
      if (treatmentDurationUnitColl?.length <= 0) {
        await fetchTreatmentDurationUnitList();
      }
      if (medicineDoseId > 0 || copyMedicineDoseId > 0) {
        const medicineDose = await getMedicineDoseDetail();
        await fetchMedicine(medicineDose?.medicineGenericId, "", true);
      } else {
        await fetchMedicine("0", "", true);
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
    }
  };

  // USEEFFECTS
  useEffect(() => {
    setIsLoading(true);
    fetchData();
  }, [medicineDoseId, copyMedicineDoseId]);

  return (
    <Box>
      <Box className={"main-container"}>
        {isForPopUp === false && (
          <Grid container className="page-header">
            <Grid item xs={6} sm={6} md={6}>
              <Box
                sx={{
                  display: "flex",
                  height: "100%",
                  alignMedicines: "center",
                }}
              >
                <CustomBreadcrumb />
              </Box>
            </Grid>
            <Grid item xs={6} sm={6} md={6}>
              <Box className="page-header-icon">
                <Button
                  color="primary"
                  variant="outlined"
                  type="button"
                  className="secondary-button"
                  startIcon={<ArrowBackIcon />}
                  onClick={handleBack}
                >
                  Back
                </Button>
              </Box>
            </Grid>
          </Grid>
        )}
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)} action="">
            <Grid container gap={2}>
              <Grid item xs={12} className="group-container">
                <Grid container item spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h6">Medicine Information</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} lg={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.medicineDoseUnderModule
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Under module
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input"
                      >
                        <Controller
                          control={methods.control}
                          name="medicineDoseUnderModule"
                          render={({ field }) => (
                            <CustomSelect
                              {...field}
                              isNotCreatable={true}
                              id={"medicineDoseUnderModule"}
                              FieldName={"medicineDoseUnderModule"}
                              options={underModules}
                              openMenuOnFocus
                              isClearable
                              isSearchable
                              menuPortalTarget={document.querySelector("body")}
                              handleChange={(e) => {
                                methods.setValue(
                                  "medicineDoseUnderModule",
                                  e !== null ? e : null,
                                  { shouldValidate: true },
                                );
                              }}
                              className={`text-input ${
                                methods.formState.errors.medicineDoseUnderModule
                                  ? "error"
                                  : ""
                              }`}
                            />
                          )}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {
                          methods.formState.errors.medicineDoseUnderModule
                            ?.message
                        }
                      </Typography>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} lg={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.medicineDoseTitle
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Title *
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input"
                      >
                        <CustomTextField
                          placeholder="Ex. PCM for Fever or PCM for Pain"
                          autoCapitalize="none"
                          type="text"
                          fullWidth
                          id="medicineDoseTitle"
                          size="small"
                          autoFocus
                          {...methods.register("medicineDoseTitle", {
                            onChange: (e) => {
                              const input = e.target;
                              const selectionStart = input.selectionStart;
                              const selectionEnd = input.selectionEnd;

                              // CONVERT INPUT INTO UPPERCASE LATTERS
                              e.target.value = input.value
                                ? input.value.toUpperCase()
                                : "";

                              // Restore the cursor position
                              setTimeout(() => {
                                input.setSelectionRange(
                                  selectionStart,
                                  selectionEnd,
                                );
                              });

                              methods.setValue(
                                "medicineDoseTitle",
                                e.target.value,
                                {
                                  shouldValidate: true,
                                },
                              ); // UPDATE VALUE
                            },
                          })}
                          error={!!methods.formState.errors.medicineDoseTitle}
                          className={`text-input ${
                            methods.formState.errors.medicineDoseTitle
                              ? "error"
                              : ""
                          }`}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.medicineDoseTitle?.message}
                      </Typography>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} lg={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.medicineGenericId
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Dose deciding content/Generic name{" "}
                        <CustomTooltip
                          title="This field is important for better recored management"
                          hideHeader={true}
                        ></CustomTooltip>
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input"
                      >
                        <Controller
                          control={methods.control}
                          name="medicineGenericId"
                          render={({ field }) => (
                            <CustomAsyncSelect
                              {...field}
                              isNotCreatable={false}
                              id={"medicineGenericId"}
                              FieldName={"medicineGenericId"}
                              defaultOptions={medicineGenericNameColl}
                              openMenuOnFocus
                              isClearable
                              isSearchable
                              isDisabled={
                                // (medicineDoseId ?? 0) > 0 ||
                                genericNameObject ? true : false
                              }
                              loadOptions={loadMedicineGenericNameList}
                              menuPortalTarget={document.querySelector("body")}
                              handleChange={async (e) => {
                                if (e?.__isNew__ === true) {
                                  handleClickOpen(
                                    true,
                                    e.label,
                                    "MEDICINE_GENERIC_NAME",
                                    e,
                                  );
                                } else if (
                                  methods
                                    ?.getValues()
                                    ?.medicineGenericId?.value?.toString() !==
                                  e?.value?.toString()
                                ) {
                                  methods.setValue(
                                    "medicineGenericId",
                                    e?.value ? e : null,
                                    {
                                      shouldValidate: true,
                                    },
                                  );
                                  methods.setValue(
                                    "medicineGenericName",
                                    e?.value ? e?.label : "",
                                    {
                                      shouldValidate: true,
                                    },
                                  );
                                  // methods.setValue(
                                  //   "medicineDoseMedicineColl",
                                  //   [],
                                  //   {
                                  //     shouldValidate: true,
                                  //   }
                                  // );
                                  handleDeleteMedicine("ALL");
                                  if (e?.value) {
                                    setIsLoading(true);
                                    await fetchMedicine(e?.value, "", true);
                                    setIsLoading(false);
                                  } else {
                                    setIsLoading(true);
                                    await fetchMedicine("0", "", true);
                                    setIsLoading(false);
                                  }
                                }
                              }}
                            />
                          )}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.medicineGenericId?.message}
                      </Typography>
                    </FormControl>
                  </Grid>

                  {/* <Grid item xs={12} sm={6} lg={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${methods.formState.errors.doseDecidingContent
                          ? "validation-message"
                          : ""
                          }`}
                      >
                        Dose deciding content/Generic name
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input"
                      >
                        <CustomTextField
                          autoCapitalize="none"
                          type="text"
                          fullWidth
                          id="doseDecidingContent"
                          size="small"
                          {...methods.register("doseDecidingContent", {
                            onChange: (e) => {
                              const input = e.target;
                              const selectionStart = input.selectionStart;
                              const selectionEnd = input.selectionEnd;

                              // CONVERT INPUT INTO UPPERCASE LATTERS
                              e.target.value = input.value
                                ? input.value.toUpperCase()
                                : "";

                              // Restore the cursor position
                              setTimeout(() => {
                                input.setSelectionRange(
                                  selectionStart,
                                  selectionEnd
                                );
                              });

                              methods.setValue(
                                "doseDecidingContent",
                                e.target.value,
                                {
                                  shouldValidate: true,
                                }
                              ); // UPDATE VALUE
                            },
                          })}
                          error={!!methods.formState.errors.doseDecidingContent}
                          className={`text-input ${methods.formState.errors.doseDecidingContent
                            ? "error"
                            : ""
                            }`}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.doseDecidingContent?.message}
                      </Typography>
                    </FormControl>
                  </Grid> */}
                  <Grid item xs={12} sm={6} lg={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.medicineUsedAsId
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Used as
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input"
                      >
                        <Controller
                          control={methods.control}
                          name="medicineUsedAsId"
                          render={({ field }) => (
                            <CustomAsyncSelect
                              {...field}
                              isNotCreatable={false}
                              id={"medicineUsedAsId"}
                              FieldName={"medicineUsedAsId"}
                              defaultOptions={medicineUsedAsColl}
                              openMenuOnFocus
                              isClearable
                              isSearchable
                              loadOptions={loadUsedAsList}
                              menuPortalTarget={document.querySelector("body")}
                              handleChange={(e) => {
                                if (e?.__isNew__ === true) {
                                  handleClickOpen(
                                    true,
                                    e.label,
                                    "MEDICINE_USED_AS",
                                    e,
                                  );
                                } else if (
                                  methods
                                    ?.getValues()
                                    ?.medicineUsedAsId?.value?.toString() !==
                                  e?.value?.toString()
                                ) {
                                  methods.setValue(
                                    "medicineUsedAsId",
                                    e?.value ? e : null,
                                    {
                                      shouldValidate: true,
                                    },
                                  );
                                  methods.setValue(
                                    "medicineUsedAsName",
                                    e?.value ? e?.label : null,
                                    {
                                      shouldValidate: true,
                                    },
                                  );
                                }
                              }}
                            />
                          )}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.medicineUsedAsId?.message}
                      </Typography>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.departmentId
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Department *
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input"
                      >
                        <Controller
                          control={methods.control}
                          name="departmentId"
                          render={({ field }) => (
                            <CustomSelect
                              {...field}
                              isNotCreatable={true}
                              id={"departmentId"}
                              FieldName={"departmentId"}
                              options={departmentData}
                              isDisabled={false}
                              openMenuOnFocus
                              isClearable
                              isSearchable
                              menuPortalTarget={document.querySelector("body")}
                              handleChange={async (e: any) => {
                                if (
                                  methods
                                    ?.getValues()
                                    ?.departmentId?.value?.toString() !==
                                  e?.value?.toString()
                                ) {
                                  methods.setValue(
                                    "departmentId",
                                    e?.value ? e : null,
                                    {
                                      shouldValidate: true,
                                    },
                                  );
                                  methods.setValue("specialityId", null, {
                                    shouldValidate: true,
                                  });
                                  if (e?.value) {
                                    setIsLoading(true);
                                    await fetchSpeciality(e.value);
                                    setIsLoading(false);
                                  } else {
                                    setSpecialityData([]);
                                  }
                                }
                              }}
                              className={`text-input ${
                                methods.formState.errors.departmentId
                                  ? "error"
                                  : ""
                              }`}
                            />
                          )}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.departmentId?.message}
                      </Typography>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.specialityId
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Speciality *
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input"
                      >
                        <Controller
                          control={methods.control}
                          name="specialityId"
                          render={({ field }) => (
                            <CustomSelect
                              {...field}
                              isNotCreatable={true}
                              id={"specialityId"}
                              FieldName={"specialityId"}
                              options={specialityData}
                              openMenuOnFocus
                              isClearable
                              isDisabled={false}
                              isSearchable
                              menuPortalTarget={document.querySelector("body")}
                              handleChange={async (e: any) => {
                                methods.setValue(
                                  "specialityId",
                                  e?.value ? e : null,
                                  {
                                    shouldValidate: true,
                                  },
                                );
                              }}
                              className={`text-input ${
                                methods.formState.errors.specialityId
                                  ? "error"
                                  : ""
                              }`}
                            />
                          )}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.specialityId?.message}
                      </Typography>
                    </FormControl>
                  </Grid>
                  <Grid
                    item
                    sm={6}
                    lg={9}
                    sx={{
                      display: {
                        xs: "none",
                        sm: "grid",
                      },
                    }}
                  ></Grid>
                  {isFrom !== "MEDICINE" && (
                    <Grid item xs={12} xl={12}>
                      <Box
                        className="template-table-container"
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          border: "1px solid #0000001f",
                          borderRadius: 4,
                        }}
                      >
                        <Box className="inner-title-container">
                          <Box
                            className="template-box-sm"
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              backgroundColor: "#f1f5f8",
                            }}
                          >
                            <Grid container>
                              <Grid item xs={12} lg={4}>
                                <Typography>Medicine(s)</Typography>
                              </Grid>
                              <Grid
                                item
                                lg={2}
                                display={{
                                  xs: "none",
                                  lg: "grid",
                                }}
                              >
                                <Typography>
                                  {" "}
                                  Strength of dose deciding Content
                                </Typography>
                              </Grid>
                              <Grid
                                item
                                lg={3}
                                display={{
                                  xs: "none",
                                  lg: "grid",
                                }}
                              >
                                <Typography> Dose deciding content</Typography>
                              </Grid>
                              <Grid
                                item
                                lg={1.5}
                                display={{
                                  xs: "none",
                                  lg: "grid",
                                }}
                              >
                                <Typography> Print Unit</Typography>
                              </Grid>
                              <Grid
                                item
                                lg={1.5}
                                display={{
                                  xs: "none",
                                  lg: "grid",
                                }}
                              >
                                <Typography>Package</Typography>
                              </Grid>
                            </Grid>
                            <Typography
                              className="text-link-danger link-cursor"
                              onClick={(e) => {
                                handleDeleteMedicine("ALL");
                              }}
                            >
                              CLEAR
                            </Typography>
                          </Box>
                          <DraggableList
                            items={filteredMedicineDoseMedicineColl || []}
                            setItem={(e) => {
                              console.log(e);
                            }}
                            Styledcard={MedicineCard}
                            className="draggable-list-container-scrollable"
                          />
                          <Box
                            style={{
                              padding: 10,
                              paddingLeft: 20,
                              paddingRight: 20,
                            }}
                          >
                            <FormControl fullWidth>
                              <SkeletonWrapper
                                isLoading={isLoading}
                                className="skeleton-input"
                              >
                                <Controller
                                  control={methods.control}
                                  name="fileteredMedicineDoseMedicineColl"
                                  render={({ field }) => (
                                    <CustomAsyncSelect
                                      {...field}
                                      id={"medicineId"}
                                      isNotCreatable={false}
                                      placeholder="Search or create Medicine "
                                      FieldName={
                                        "fileteredMedicineDoseMedicineColl"
                                      }
                                      defaultOptions={medicineColl}
                                      openMenuOnFocus
                                      hideSelectedOptions
                                      controlShouldRenderValue={false}
                                      closeMenuOnSelect={false}
                                      isMulti
                                      isClearable={false}
                                      backspaceRemovesValue={false}
                                      isSearchable
                                      loadOptions={(input, callback) => {
                                        loadMedicineList(
                                          methods.getValues("medicineGenericId")
                                            ?.value ?? "0",
                                          input?.trim(),
                                          callback,
                                        );
                                      }}
                                      menuPortalTarget={document.querySelector(
                                        "body",
                                      )}
                                      value={
                                        filteredMedicineDoseMedicineColl || []
                                      }
                                      onCreateOption={(value) => {
                                        handleClickOpen(
                                          true,
                                          value,
                                          "MEDICINE",
                                          methods?.getValues()
                                            ?.medicineGenericId || null,
                                          0,
                                        );
                                      }}
                                      CustomOptionComponent={
                                        medicineOptionComponent
                                      }
                                      customMenuList={medicineMenuListComponent}
                                      handleChange={(e) => {
                                        let length = e?.length || 0;
                                        if (length > 0) {
                                          handleMedicineAdd(e[length - 1]);
                                        }
                                      }}
                                    />
                                  )}
                                />
                              </SkeletonWrapper>
                              <Typography className="validation-message">
                                {
                                  methods.formState.errors
                                    ?.fileteredMedicineDoseMedicineColl?.message
                                }
                              </Typography>
                            </FormControl>
                          </Box>
                        </Box>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Grid>
              <Grid item xs={12} className="group-container">
                <Grid container item spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h6">
                      Weight/BSA dependent dose
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={4} md={3} xl={1.5}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors
                            .weightBsaDependentMinDoseValue
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Minimum dose{" "}
                        <CustomTooltip
                          title="This field is important for automated dose calculation"
                          hideHeader={true}
                        ></CustomTooltip>
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input"
                      >
                        <CustomTextField
                          placeholder="0.0000"
                          fullWidth
                          id="weightBsaDependentMinDoseValue"
                          size="small"
                          inputProps={numberProps}
                          {...methods.register(
                            "weightBsaDependentMinDoseValue",
                            {
                              onChange(event) {
                                methods.setValue(
                                  "weightBsaDependentMinDoseValue",
                                  event.target.value,
                                  {
                                    shouldValidate: true,
                                  },
                                );
                              },
                            },
                          )}
                          onFocus={handleFocus}
                          error={
                            !!methods.formState.errors
                              .weightBsaDependentMinDoseValue
                          }
                          className={`text-input ${
                            methods.formState.errors
                              .weightBsaDependentMinDoseValue
                              ? "error"
                              : ""
                          }`}
                        />
                      </SkeletonWrapper>
                    </FormControl>
                    <Typography className="validation-message">
                      {
                        methods.formState.errors.weightBsaDependentMinDoseValue
                          ?.message
                      }
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    sm={8}
                    md={9}
                    xl={10.5}
                    display={{
                      xs: "none",
                      sm: "grid",
                    }}
                  ></Grid>
                  <Grid item xs={12} sx={{ paddingTop: "2px !important" }}>
                    <Typography className="unit-type-medicine-dose">
                      {methods.getValues()?.weightBsaDependentMinDoseValue ||
                        null}{" "}
                      {methods.getValues()
                        ?.weightBsaDependentDesiredDoseUnitName || ""}
                      {methods.getValues().weightBsaDependentPerWeightBsaUnit
                        ?.value !== "NONE" &&
                        "/" +
                          (methods.getValues()
                            ?.weightBsaDependentPerWeightBsaUnitName || "")}
                      /
                      {methods.getValues()
                        ?.weightBsaDependentPerIntervalUnitName || ""}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={9} md={6} lg={5} xl={4}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors
                            .weightBsaDependentDesiredDoseValue
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Desired dose{" "}
                        <CustomTooltip
                          title="This field is important for automated dose calculation"
                          hideHeader={true}
                        ></CustomTooltip>
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input"
                      >
                        <CustomTextField
                          placeholder="0.0000"
                          fullWidth
                          id="weightBsaDependentDesiredDoseValue"
                          size="small"
                          inputProps={numberProps}
                          {...methods.register(
                            "weightBsaDependentDesiredDoseValue",
                            {
                              onChange(event) {
                                methods.setValue(
                                  "weightBsaDependentDesiredDoseValue",
                                  event.target.value,
                                  {
                                    shouldValidate: true,
                                  },
                                );
                              },
                            },
                          )}
                          onFocus={handleFocus}
                          InputProps={{
                            endAdornment: smd ? (
                              <></>
                            ) : (
                              <InputAdornment position="end">
                                <Box
                                  sx={{
                                    display: "flex",
                                  }}
                                >
                                  <Box
                                    className="input-end-andorment-border"
                                    sx={{
                                      minWidth: "100px",
                                    }}
                                  >
                                    <CustomSelect
                                      openMenuOnFocus
                                      value={methods.getValues(
                                        "weightBsaDependentDesiredDoseUnit",
                                      )}
                                      menuPortalTarget={document.querySelector(
                                        "body",
                                      )}
                                      handleChange={(e) => {
                                        methods.setValue(
                                          "weightBsaDependentDesiredDoseUnit",
                                          e || null,
                                          { shouldValidate: true },
                                        );
                                        methods.setValue(
                                          "weightBsaDependentDesiredDoseUnitName",
                                          e?.label || null,
                                          { shouldValidate: true },
                                        );
                                      }}
                                      options={strengthUnitColl}
                                      isNotCreatable={true}
                                      isIndicatorSeparator={false}
                                      isBorderNone
                                      id={"weightBsaDependentDesiredDoseUnit"}
                                      FieldName={
                                        "weightBsaDependentDesiredDoseUnit"
                                      }
                                    />
                                  </Box>
                                  <Box
                                    className="input-end-andorment-border"
                                    sx={{
                                      minWidth: "100px",
                                    }}
                                  >
                                    <CustomSelect
                                      openMenuOnFocus
                                      value={methods.getValues(
                                        "weightBsaDependentPerWeightBsaUnit",
                                      )}
                                      menuPortalTarget={document.querySelector(
                                        "body",
                                      )}
                                      handleChange={(e) => {
                                        methods.setValue(
                                          "weightBsaDependentPerWeightBsaUnit",
                                          e || null,
                                          { shouldValidate: true },
                                        );
                                        methods.setValue(
                                          "weightBsaDependentPerWeightBsaUnitName",
                                          e?.label || null,
                                          { shouldValidate: true },
                                        );
                                      }}
                                      options={unitsOfPerWeightBSA}
                                      isNotCreatable={true}
                                      isIndicatorSeparator={false}
                                      isBorderNone
                                      id={"weightBsaDependentPerWeightBsaUnit"}
                                      FieldName={
                                        "weightBsaDependentPerWeightBsaUnit"
                                      }
                                    />
                                  </Box>
                                  <Box
                                    className="input-end-andorment-border"
                                    sx={{
                                      minWidth: "100px",
                                    }}
                                  >
                                    <CustomSelect
                                      openMenuOnFocus
                                      value={methods.getValues(
                                        "weightBsaDependentPerIntervalUnit",
                                      )}
                                      menuPortalTarget={document.querySelector(
                                        "body",
                                      )}
                                      handleChange={(e) => {
                                        methods.setValue(
                                          "weightBsaDependentPerIntervalUnit",
                                          e || null,
                                          { shouldValidate: true },
                                        );
                                        methods.setValue(
                                          "weightBsaDependentPerIntervalUnitName",
                                          e?.label || null,
                                          { shouldValidate: true },
                                        );
                                      }}
                                      options={perIntervalUnitColl}
                                      isNotCreatable={true}
                                      isIndicatorSeparator={false}
                                      isBorderNone
                                      id={"weightBsaDependentPerIntervalUnit"}
                                      FieldName={
                                        "weightBsaDependentPerIntervalUnit"
                                      }
                                    />
                                  </Box>
                                </Box>
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            minWidth: "150px",
                            ".MuiOutlinedInput-root": {
                              paddingRight: mdd ? "" : 0,
                            },
                          }}
                          error={
                            !!methods.formState.errors
                              .weightBsaDependentDesiredDoseValue
                          }
                        />
                      </SkeletonWrapper>
                    </FormControl>
                    <Typography className="validation-message">
                      {
                        methods.formState.errors
                          .weightBsaDependentDesiredDoseValue?.message
                      }
                    </Typography>
                  </Grid>

                  <Grid
                    item
                    sm={3}
                    md={6}
                    display={{
                      xs: "none",
                      sm: "grid",
                    }}
                  ></Grid>

                  <Grid
                    item
                    xs={12}
                    display={{
                      sm: "none",
                    }}
                  >
                    <FormLabel
                      className={`fileds-label-text  ${
                        methods.formState.errors
                          .weightBsaDependentDesiredDoseUnit
                          ? "validation-message"
                          : ""
                      }`}
                    >
                      Desired Units *
                    </FormLabel>
                    <Box className="small-scrren-uints">
                      <Box className="width-100-percent">
                        <CustomSelect
                          openMenuOnFocus
                          value={methods.getValues(
                            "weightBsaDependentDesiredDoseUnit",
                          )}
                          menuPortalTarget={document.querySelector("body")}
                          handleChange={(e) => {
                            methods.setValue(
                              "weightBsaDependentDesiredDoseUnit",
                              e || null,
                              { shouldValidate: true },
                            );
                            methods.setValue(
                              "weightBsaDependentDesiredDoseUnitName",
                              e?.label || null,
                              { shouldValidate: true },
                            );
                          }}
                          options={strengthUnitColl}
                          isNotCreatable={true}
                          isIndicatorSeparator={false}
                          isBorderNone
                          id={"weightBsaDependentDesiredDoseUnit"}
                          FieldName={"weightBsaDependentDesiredDoseUnit"}
                        />
                      </Box>
                      <Box className="select-border width-100-percent">
                        <CustomSelect
                          openMenuOnFocus
                          value={methods.getValues(
                            "weightBsaDependentPerWeightBsaUnit",
                          )}
                          menuPortalTarget={document.querySelector("body")}
                          handleChange={(e) => {
                            methods.setValue(
                              "weightBsaDependentPerWeightBsaUnit",
                              e || null,
                              { shouldValidate: true },
                            );
                            methods.setValue(
                              "weightBsaDependentPerWeightBsaUnitName",
                              e?.label || null,
                              { shouldValidate: true },
                            );
                          }}
                          options={unitsOfPerWeightBSA}
                          isNotCreatable={true}
                          isIndicatorSeparator={false}
                          isBorderNone
                          id={"weightBsaDependentPerWeightBsaUnit"}
                          FieldName={"weightBsaDependentPerWeightBsaUnit"}
                        />
                      </Box>
                      <Box className="select-border width-100-percent">
                        <CustomSelect
                          openMenuOnFocus
                          value={methods.getValues(
                            "weightBsaDependentPerIntervalUnit",
                          )}
                          menuPortalTarget={document.querySelector("body")}
                          handleChange={(e) => {
                            methods.setValue(
                              "weightBsaDependentPerIntervalUnit",
                              e || null,
                              { shouldValidate: true },
                            );
                            methods.setValue(
                              "weightBsaDependentPerIntervalUnitName",
                              e?.label || null,
                              { shouldValidate: true },
                            );
                          }}
                          options={perIntervalUnitColl}
                          isNotCreatable={true}
                          isIndicatorSeparator={false}
                          isBorderNone
                          id={"weightBsaDependentPerIntervalUnit"}
                          FieldName={"weightBsaDependentPerIntervalUnit"}
                        />
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sx={{ paddingTop: "2px !important" }}>
                    <Typography className="unit-type-medicine-dose">
                      {methods.getValues()
                        ?.weightBsaDependentDesiredDoseValue || null}{" "}
                      {methods.getValues()
                        ?.weightBsaDependentDesiredDoseUnitName || ""}
                      {methods.getValues().weightBsaDependentPerWeightBsaUnit
                        ?.value !== "NONE" &&
                        "/" +
                          (methods.getValues()
                            ?.weightBsaDependentPerWeightBsaUnitName || "")}
                      /
                      {methods.getValues()
                        ?.weightBsaDependentPerIntervalUnitName || ""}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={4} md={3} xl={1.5}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors
                            .weightBsaDependentMaxDoseValue
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Maximum dose{" "}
                        <CustomTooltip
                          title="This field is important for automated dose calculation"
                          hideHeader={true}
                        ></CustomTooltip>
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input"
                      >
                        <CustomTextField
                          placeholder="0.0000"
                          fullWidth
                          id="weightBsaDependentMaxDoseValue"
                          size="small"
                          inputProps={numberProps}
                          {...methods.register(
                            "weightBsaDependentMaxDoseValue",
                            {
                              onChange(event) {
                                methods.setValue(
                                  "weightBsaDependentMaxDoseValue",
                                  event.target.value,
                                  {
                                    shouldValidate: true,
                                  },
                                );
                              },
                            },
                          )}
                          onFocus={handleFocus}
                          error={
                            !!methods.formState.errors
                              .weightBsaDependentMaxDoseValue
                          }
                          className={`text-input ${
                            methods.formState.errors
                              .weightBsaDependentMaxDoseValue
                              ? "error"
                              : ""
                          }`}
                        />
                      </SkeletonWrapper>
                    </FormControl>
                    <Typography className="validation-message">
                      {
                        methods.formState.errors.weightBsaDependentMaxDoseValue
                          ?.message
                      }
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sx={{ paddingTop: "2px !important" }}>
                    <Typography className="unit-type-medicine-dose">
                      {methods.getValues()?.weightBsaDependentMaxDoseValue ||
                        null}{" "}
                      {methods.getValues()
                        ?.weightBsaDependentDesiredDoseUnitName || ""}
                      {methods.getValues().weightBsaDependentPerWeightBsaUnit
                        ?.value !== "NONE" &&
                        "/" +
                          (methods.getValues()
                            ?.weightBsaDependentPerWeightBsaUnitName || "")}
                      /
                      {methods.getValues()
                        ?.weightBsaDependentPerIntervalUnitName || ""}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} className="group-container">
                <Grid container item spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h6">
                      Fixed dose (Weight/BSA independent)
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={8} md={6} xl={4}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.fixedDoseValue
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Fixed dose{" "}
                        <CustomTooltip
                          title="This field is important for automated dose calculation"
                          hideHeader={true}
                        ></CustomTooltip>
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input"
                      >
                        <CustomTextField
                          placeholder="0.0000"
                          fullWidth
                          id="fixedDoseValue"
                          size="small"
                          inputProps={numberProps}
                          {...methods.register("fixedDoseValue", {
                            onChange(event) {
                              methods.setValue(
                                "fixedDoseValue",
                                event.target.value,
                                {
                                  shouldValidate: true,
                                },
                              );
                            },
                          })}
                          onFocus={handleFocus}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <Box
                                  sx={{
                                    display: "flex",
                                  }}
                                >
                                  <Box
                                    className="input-end-andorment-border"
                                    sx={{
                                      minWidth: "100px",
                                    }}
                                  >
                                    <CustomSelect
                                      openMenuOnFocus
                                      value={methods.getValues("fixedDoseUnit")}
                                      menuPortalTarget={document.querySelector(
                                        "body",
                                      )}
                                      handleChange={(e) => {
                                        methods.setValue(
                                          "fixedDoseUnit",
                                          e || null,
                                          { shouldValidate: true },
                                        );
                                        methods.setValue(
                                          "fixedDoseUnitName",
                                          e?.label || null,
                                          { shouldValidate: true },
                                        );
                                      }}
                                      options={strengthUnitColl}
                                      isNotCreatable={true}
                                      isIndicatorSeparator={false}
                                      isBorderNone
                                      id={"fixedDoseUnit"}
                                      FieldName={"fixedDoseUnit"}
                                    />
                                  </Box>
                                  <Box
                                    className="input-end-andorment-border"
                                    sx={{
                                      minWidth: "100px",
                                    }}
                                  >
                                    <CustomSelect
                                      openMenuOnFocus
                                      value={methods.getValues(
                                        "fixedDosePerIntervalUnit",
                                      )}
                                      menuPortalTarget={document.querySelector(
                                        "body",
                                      )}
                                      handleChange={(e) => {
                                        methods.setValue(
                                          "fixedDosePerIntervalUnit",
                                          e || null,
                                          { shouldValidate: true },
                                        );
                                        methods.setValue(
                                          "fixedDosePerIntervalUnitName",
                                          e?.label || null,
                                          { shouldValidate: true },
                                        );
                                      }}
                                      options={perIntervalUnitColl}
                                      isNotCreatable={true}
                                      isIndicatorSeparator={false}
                                      isBorderNone
                                      id={"fixedDosePerIntervalUnit"}
                                      FieldName={"fixedDosePerIntervalUnit"}
                                    />
                                  </Box>
                                </Box>
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            minWidth: "150px",
                            ".MuiOutlinedInput-root": {
                              paddingRight: 0,
                            },
                          }}
                          error={!!methods.formState.errors.fixedDoseValue}
                        />
                      </SkeletonWrapper>
                    </FormControl>
                    <Typography className="validation-message">
                      {methods.formState.errors.fixedDoseValue?.message}
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    sm={4}
                    md={6}
                    xl={8}
                    display={{
                      xs: "none",
                      sm: "grid",
                    }}
                  ></Grid>
                  <Grid item xs={12} sx={{ paddingTop: "2px !important" }}>
                    <Typography className="unit-type-medicine-dose">
                      {methods.getValues()?.fixedDoseValue || null}{" "}
                      {methods.getValues()?.fixedDoseUnitName || ""}/
                      {methods.getValues()?.fixedDosePerIntervalUnitName || ""}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={8} md={6} xl={4}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.maxFixedDoseValue
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Maximum fixed dose{" "}
                        <CustomTooltip
                          title="This field is important for automated dose calculation"
                          hideHeader={true}
                        ></CustomTooltip>
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input"
                      >
                        <CustomTextField
                          placeholder="0.0000"
                          fullWidth
                          id="maxFixedDoseValue"
                          size="small"
                          inputProps={numberProps}
                          {...methods.register("maxFixedDoseValue", {
                            onChange(event) {
                              methods.setValue(
                                "maxFixedDoseValue",
                                event.target.value,
                                {
                                  shouldValidate: true,
                                },
                              );
                            },
                          })}
                          onFocus={handleFocus}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <Box
                                  sx={{
                                    display: "flex",
                                  }}
                                >
                                  <Box
                                    className="input-end-andorment-border"
                                    sx={{
                                      minWidth: "100px",
                                    }}
                                  >
                                    <CustomSelect
                                      openMenuOnFocus
                                      value={methods.getValues(
                                        "maxFixedDoseUnit",
                                      )}
                                      menuPortalTarget={document.querySelector(
                                        "body",
                                      )}
                                      handleChange={(e) => {
                                        methods.setValue(
                                          "maxFixedDoseUnit",
                                          e || null,
                                          { shouldValidate: true },
                                        );
                                        methods.setValue(
                                          "maxFixedDoseUnitName",
                                          e?.label || null,
                                          { shouldValidate: true },
                                        );
                                      }}
                                      options={strengthUnitColl}
                                      isNotCreatable={true}
                                      isIndicatorSeparator={false}
                                      isBorderNone
                                      id={"maxFixedDoseUnit"}
                                      FieldName={"maxFixedDoseUnit"}
                                    />
                                  </Box>
                                  <Box
                                    className="input-end-andorment-border"
                                    sx={{
                                      minWidth: "100px",
                                    }}
                                  >
                                    <CustomSelect
                                      openMenuOnFocus
                                      value={methods.getValues(
                                        "maxFixedDosePerIntervalUnit",
                                      )}
                                      menuPortalTarget={document.querySelector(
                                        "body",
                                      )}
                                      handleChange={(e) => {
                                        methods.setValue(
                                          "maxFixedDosePerIntervalUnit",
                                          e || null,
                                          { shouldValidate: true },
                                        );
                                        methods.setValue(
                                          "maxFixedDosePerIntervalUnitName",
                                          e?.label || null,
                                          { shouldValidate: true },
                                        );
                                      }}
                                      options={perIntervalUnitColl}
                                      isNotCreatable={true}
                                      isIndicatorSeparator={false}
                                      isBorderNone
                                      id={"maxFixedDosePerIntervalUnit"}
                                      FieldName={"maxFixedDosePerIntervalUnit"}
                                    />
                                  </Box>
                                </Box>
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            minWidth: "150px",
                            ".MuiOutlinedInput-root": {
                              paddingRight: 0,
                            },
                          }}
                          error={!!methods.formState.errors.maxFixedDoseValue}
                        />
                      </SkeletonWrapper>
                    </FormControl>
                    <Typography className="validation-message">
                      {methods.formState.errors.maxFixedDoseValue?.message}
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    sm={4}
                    md={6}
                    xl={8}
                    display={{
                      xs: "none",
                      sm: "grid",
                    }}
                  ></Grid>
                  <Grid item xs={12} sx={{ paddingTop: "2px !important" }}>
                    <Typography className="unit-type-medicine-dose">
                      {methods.getValues()?.maxFixedDoseValue || null}{" "}
                      {methods.getValues()?.maxFixedDoseUnitName || ""}/
                      {methods.getValues()?.maxFixedDosePerIntervalUnitName ||
                        ""}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              {/* <Grid item xs={12} className="group-container">
                <Grid container item spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h6">
                      Further Dilution before administration
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl>
                      <FormControlLabel
                        control={
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <CustomSwitch
                              {...methods.register("isDilutionNeeded", {
                                onChange(event) {
                                  methods.setValue(
                                    "isDilutionNeeded",
                                    event.target.checked,
                                    {
                                      shouldValidate: true,
                                    }
                                  );
                                  methods.setValue("diluentNameId", null, {
                                    shouldValidate: true,
                                  });
                                  methods.setValue("diluentName", null, {
                                    shouldValidate: true,
                                  });
                                  methods.setValue("diluentValue", null, {
                                    shouldValidate: true,
                                  });
                                },
                              })}
                              checked={methods.getValues()?.isDilutionNeeded}
                            />
                          </SkeletonWrapper>
                        }
                        label="Needed ?"
                      />
                      <Typography className="validation-message">
                        {methods.formState.errors.isDilutionNeeded?.message}
                      </Typography>
                    </FormControl>
                  </Grid>
                  {methods.getValues().isDilutionNeeded === true && (
                    <>
                      <Grid item xs={12} sm={6} lg={4} xl={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.diluentNameId
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Diluent *
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <Controller
                              control={methods.control}
                              name="diluentNameId"
                              render={({ field }) => (
                                <CustomAsyncSelect
                                  {...field}
                                  isNotCreatable={false}
                                  id={"diluentNameId"}
                                  FieldName={"diluentNameId"}
                                  defaultOptions={diluentColl}
                                  openMenuOnFocus
                                  isClearable
                                  isSearchable
                                  loadOptions={loadDiluentList}
                                  menuPortalTarget={document.querySelector(
                                    "body"
                                  )}
                                  handleChange={(e) => {
                                    if (e?.__isNew__ === true) {
                                      handleClickOpen(
                                        true,
                                        e.label,
                                        "MEDICINE_DILUENT",
                                        e
                                      );
                                    } else if (
                                      methods
                                        ?.getValues()
                                        ?.diluentNameId?.value?.toString() !==
                                      e?.value?.toString()
                                    ) {
                                      methods.setValue(
                                        "diluentNameId",
                                        e?.value ? e : null,
                                        {
                                          shouldValidate: true,
                                        }
                                      );
                                      methods.setValue(
                                        "diluentName",
                                        e?.value ? e?.label : null,
                                        {
                                          shouldValidate: true,
                                        }
                                      );
                                    }
                                  }}
                                />
                              )}
                            />
                          </SkeletonWrapper>
                          <Typography className="validation-message">
                            {methods.formState.errors.diluentNameId?.message}
                          </Typography>
                        </FormControl>
                      </Grid>
                      <Grid
                        item
                        sm={6}
                        lg={8}
                        xl={9}
                        display={{
                          xs: "none",
                          sm: "grid",
                        }}
                      ></Grid>
                      <Grid item xs={12} sm={6} md={3} xl={2}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.diluentValue
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Amount of diluent *
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <CustomTextField
                              placeholder="0.0000"
                              fullWidth
                              id="diluentValue"
                              size="small"
                              inputProps={numberProps}
                              onFocus={handleFocus}
                              {...methods.register("diluentValue", {
                                onChange(event) {
                                  methods.setValue(
                                    "diluentValue",
                                    event.target.value,
                                    {
                                      shouldValidate: true,
                                    }
                                  );
                                },
                              })}
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <Box
                                      sx={{
                                        display: "flex",
                                      }}
                                    >
                                      <Box className="input-end-andorment-border">
                                        <Select
                                          value={
                                            methods.getValues().diluentUnit
                                          }
                                          onChange={(e) => {
                                            methods.setValue(
                                              "diluentUnit",
                                              e.target.value,
                                              { shouldValidate: true }
                                            );
                                            methods.setValue(
                                              "diluentUnitName",
                                              getLabel(
                                                e.target.value,
                                                unitsOfDiluant
                                              ),
                                              { shouldValidate: true }
                                            );
                                          }}
                                          size="small"
                                          displayEmpty
                                          inputProps={{
                                            "aria-label": "Without label",
                                          }}
                                          sx={{
                                            ".MuiOutlinedInput-notchedOutline":
                                              {
                                                border: "none",
                                              },
                                            ".MuiSelect-select": {
                                              paddingRight: "0px",
                                            },
                                          }}
                                        >
                                          {unitsOfDiluant?.map(
                                            (x: any, index: number) => (
                                              <MenuItem
                                                key={index?.toString()}
                                                value={x.value}
                                              >
                                                {x.label}
                                              </MenuItem>
                                            )
                                          )}
                                        </Select>
                                      </Box>
                                    </Box>
                                  </InputAdornment>
                                ),
                              }}
                              sx={{
                                minWidth: "150px",
                                ".MuiOutlinedInput-root": {
                                  paddingRight: 0,
                                },
                              }}
                              error={!!methods.formState.errors.diluentValue}
                            />
                          </SkeletonWrapper>
                        </FormControl>
                        <Typography className="validation-message">
                          {methods.formState.errors.diluentValue?.message}
                        </Typography>
                      </Grid>
                      <Grid
                        item
                        sm={6}
                        md={9}
                        xl={10}
                        display={{
                          xs: "none",
                          sm: "grid",
                        }}
                      ></Grid>
                      <Grid item xs={12} sx={{ paddingTop: "2px !important" }}>
                        <Typography className="unit-type-medicine-dose">
                          {methods.getValues()?.diluentValue || 0}{" "}
                          {methods.getValues().diluentUnitName || ""}
                        </Typography>
                      </Grid>
                    </>
                  )}
                </Grid>
              </Grid> */}
              <Grid item xs={12} className="group-container">
                <Grid container item spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h6">Schedule of Dosage</Typography>
                  </Grid>
                  {/* <Grid item xs={12} sm={6} lg={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.dosePrintUnit
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Print unit{" "}
                        <CustomTooltip
                          title="This field is important for automated dose calculation"
                          hideHeader={true}
                        ></CustomTooltip>
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input"
                      >
                        <Controller
                          control={methods.control}
                          name="dosePrintUnit"
                          render={({ field }) => (
                            <CustomSelect
                              {...field}
                              isNotCreatable={true}
                              id={"dosePrintUnit"}
                              FieldName={"dosePrintUnit"}
                              options={unitsForPrint}
                              openMenuOnFocus
                              isSearchable
                              menuPortalTarget={document.querySelector("body")}
                              handleChange={(e) => {
                                methods.setValue(
                                  "dosePrintUnit",
                                  e !== null ? e : null,
                                  { shouldValidate: true }
                                );
                                methods.setValue(
                                  "dosePrintUnitName",
                                  e !== null ? e?.label : null,
                                  { shouldValidate: true }
                                );
                              }}
                              className={`text-input ${
                                methods.formState.errors.dosePrintUnit
                                  ? "error"
                                  : ""
                              }`}
                            />
                          )}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.dosePrintUnit?.message}
                      </Typography>
                    </FormControl>
                  </Grid> */}
                  {/* <Grid
                    item
                    sm={6}
                    lg={9}
                    display={{
                      xs: "none",
                      sm: "grid",
                    }}
                  ></Grid> */}
                  <Grid item xs={12} sm={6} md={4} xl={2}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.durationOfTreatmentValue
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Duration of treatment
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input"
                      >
                        <CustomTextField
                          placeholder="0.0000"
                          fullWidth
                          id="durationOfTreatmentValue"
                          size="small"
                          inputProps={numberProps}
                          {...methods.register("durationOfTreatmentValue", {
                            onChange(event) {
                              methods.setValue(
                                "durationOfTreatmentValue",
                                event.target.value,
                                {
                                  shouldValidate: true,
                                },
                              );
                            },
                          })}
                          onFocus={handleFocus}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <Box
                                  sx={{
                                    display: "flex",
                                  }}
                                >
                                  <Box
                                    className="input-end-andorment-border"
                                    sx={{
                                      minWidth: "110px",
                                    }}
                                  >
                                    <CustomSelect
                                      openMenuOnFocus
                                      value={methods.getValues(
                                        "durationOfTreatmentUnit",
                                      )}
                                      menuPortalTarget={document.querySelector(
                                        "body",
                                      )}
                                      handleChange={(e) => {
                                        methods.setValue(
                                          "durationOfTreatmentUnit",
                                          e || null,
                                          { shouldValidate: true },
                                        );
                                        methods.setValue(
                                          "durationOfTreatmentUnitName",
                                          e?.label || null,
                                          { shouldValidate: true },
                                        );
                                      }}
                                      options={treatmentDurationUnitColl}
                                      isNotCreatable={true}
                                      isIndicatorSeparator={false}
                                      isBorderNone
                                      id={"durationOfTreatmentUnit"}
                                      FieldName={"durationOfTreatmentUnit"}
                                    />
                                  </Box>
                                </Box>
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            minWidth: "150px",
                            ".MuiOutlinedInput-root": {
                              paddingRight: 0,
                            },
                          }}
                          error={
                            !!methods.formState.errors.durationOfTreatmentValue
                          }
                        />
                      </SkeletonWrapper>
                    </FormControl>
                    <Typography className="validation-message">
                      {
                        methods.formState.errors.durationOfTreatmentValue
                          ?.message
                      }
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    sm={6}
                    md={8}
                    xl={10}
                    display={{
                      xs: "none",
                      sm: "grid",
                    }}
                  ></Grid>
                  <Grid item xs={12} sx={{ paddingTop: "2px !important" }}>
                    <Typography className="unit-type-medicine-dose">
                      {methods.getValues()?.durationOfTreatmentValue || 0}{" "}
                      {methods.getValues()?.durationOfTreatmentUnitName || ""}
                    </Typography>
                  </Grid>
                  {/* <Grid item xs={12} sm={6} md={4} xl={2}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.relationToFoodTimeGapValue
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Time gap
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input"
                      >
                        <CustomTextField
                          placeholder="0.0000"
                          fullWidth
                          id="relationToFoodTimeGapValue"
                          size="small"
                          inputProps={numberProps}
                          {...methods.register("relationToFoodTimeGapValue", {
                            onChange(event) {
                              methods.setValue(
                                "relationToFoodTimeGapValue",
                                event.target.value,
                                {
                                  shouldValidate: true,
                                }
                              );
                            },
                          })}
                          onFocus={handleFocus}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <Box
                                  sx={{
                                    display: "flex",
                                  }}
                                >
                                  <Box className="input-end-andorment-border">
                                    <Select
                                      value={
                                        methods.getValues()
                                          .relationToFoodTimeGapUnit
                                      }
                                      onChange={(e) => {
                                        methods.setValue(
                                          "relationToFoodTimeGapUnit",
                                          e.target.value,
                                          { shouldValidate: true }
                                        );
                                        methods.setValue(
                                          "relationToFoodTimeGapUnitName",
                                          getLabel(e.target.value, unitsOfTime),
                                          { shouldValidate: true }
                                        );
                                      }}
                                      size="small"
                                      displayEmpty
                                      inputProps={{
                                        "aria-label": "Without label",
                                      }}
                                      sx={{
                                        ".MuiOutlinedInput-notchedOutline": {
                                          border: "none",
                                        },
                                        ".MuiSelect-select": {
                                          paddingRight: "0px",
                                        },
                                      }}
                                    >
                                      {unitsOfTime?.map(
                                        (x: any, index: number) => (
                                          <MenuItem
                                            key={index?.toString()}
                                            value={x.value}
                                          >
                                            {x.label}
                                          </MenuItem>
                                        )
                                      )}
                                    </Select>
                                  </Box>
                                </Box>
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            minWidth: "150px",
                            ".MuiOutlinedInput-root": {
                              paddingRight: 0,
                            },
                          }}
                          error={
                            !!methods.formState.errors
                              .relationToFoodTimeGapValue
                          }
                        />
                      </SkeletonWrapper>
                    </FormControl>
                    <Typography className="validation-message">
                      {
                        methods.formState.errors.relationToFoodTimeGapValue
                          ?.message
                      }
                    </Typography>
                  </Grid> */}
                  <Grid item xs={12} sm={6} md={4} xl={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.relationToFoodId
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Relation to food/Sleep
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input"
                      >
                        <Controller
                          control={methods.control}
                          name="relationToFoodId"
                          render={({ field }) => (
                            <CustomAsyncSelect
                              {...field}
                              isNotCreatable={false}
                              id={"relationToFoodId"}
                              FieldName={"relationToFoodId"}
                              defaultOptions={relationToFoodColl}
                              openMenuOnFocus
                              isClearable
                              isSearchable
                              loadOptions={loadRelationToFoodList}
                              menuPortalTarget={document.querySelector("body")}
                              handleChange={(e) => {
                                if (e?.__isNew__ === true) {
                                  handleClickOpen(
                                    true,
                                    e.label,
                                    "RELATION_TO_FOOD",
                                    e,
                                  );
                                } else if (
                                  methods
                                    ?.getValues()
                                    ?.relationToFoodId?.value?.toString() !==
                                  e?.value?.toString()
                                ) {
                                  methods.setValue(
                                    "relationToFoodId",
                                    e?.value ? e : null,
                                    {
                                      shouldValidate: true,
                                    },
                                  );
                                  methods.setValue(
                                    "relationToFoodName",
                                    e?.value ? e?.label : null,
                                    {
                                      shouldValidate: true,
                                    },
                                  );
                                }
                              }}
                            />
                          )}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.relationToFoodId?.message}
                      </Typography>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sx={{ paddingTop: "2px !important" }}>
                    <Typography className="unit-type-medicine-dose">
                      {/* {methods.getValues()?.relationToFoodTimeGapValue || 0}{" "}
                      {methods.getValues()?.relationToFoodTimeGapUnitName || ""}{" "} */}
                      {methods.getValues()?.relationToFoodName || ""}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} lg={4} xl={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.routeOfAdministrationId
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Route of administration
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input"
                      >
                        <Controller
                          control={methods.control}
                          name="routeOfAdministrationId"
                          render={({ field }) => (
                            <CustomAsyncSelect
                              {...field}
                              isNotCreatable={false}
                              id={"routeOfAdministrationId"}
                              FieldName={"routeOfAdministrationId"}
                              defaultOptions={routeOfAdministrationColl}
                              openMenuOnFocus
                              isClearable
                              isSearchable
                              loadOptions={loadRouteOfAdministrationList}
                              menuPortalTarget={document.querySelector("body")}
                              handleChange={async (e) => {
                                if (e?.__isNew__ === true) {
                                  handleClickOpen(
                                    true,
                                    e.label,
                                    "MEDICINE_ROUTE",
                                    "",
                                  );
                                } else if (
                                  methods
                                    ?.getValues()
                                    ?.routeOfAdministrationId?.value?.toString() !==
                                  e?.value?.toString()
                                ) {
                                  methods.setValue(
                                    "routeOfAdministrationId",
                                    e?.value ? e : null,
                                    {
                                      shouldValidate: true,
                                    },
                                  );
                                  methods.setValue(
                                    "routeOfAdministrationName",
                                    e?.value ? e?.label : null,
                                    {
                                      shouldValidate: true,
                                    },
                                  );
                                }
                              }}
                            />
                          )}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {
                          methods.formState.errors.routeOfAdministrationId
                            ?.message
                        }
                      </Typography>
                    </FormControl>
                  </Grid>
                  <Grid
                    item
                    sm={6}
                    lg={8}
                    xl={9}
                    display={{
                      xs: "none",
                      sm: "grid",
                    }}
                  ></Grid>
                  <Grid item xs={12} sm={6} lg={4} xl={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.doseFrequency
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Frequency{" "}
                        <CustomTooltip
                          title="This field is important for automated dose calculation"
                          hideHeader={true}
                        ></CustomTooltip>
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input"
                      >
                        <Controller
                          control={methods.control}
                          name="doseFrequency"
                          render={({ field }) => (
                            <CustomSelect
                              {...field}
                              isNotCreatable={true}
                              id={"doseFrequency"}
                              FieldName={"doseFrequency"}
                              options={frequencyUnitColl}
                              openMenuOnFocus
                              isClearable
                              isSearchable
                              menuPortalTarget={document.querySelector("body")}
                              handleChange={(e) => {
                                if (
                                  methods
                                    ?.getValues()
                                    ?.doseFrequency?.value?.toString() !==
                                  e?.value?.toString()
                                ) {
                                  if (!["SOS"].includes(e?.value)) {
                                    methods.setValue(
                                      "indicationOfSosMedicineId",
                                      null,
                                    );
                                    methods.setValue(
                                      "indicationOfSosMedicineName",
                                      null,
                                    );
                                  }
                                  methods.setValue(
                                    "doseFrequencyName",
                                    e?.value ? e?.label : null,
                                  );
                                  methods.setValue(
                                    "doseFrequency",
                                    e?.value ? e : null,
                                    {
                                      shouldValidate: true,
                                    },
                                  );
                                }
                              }}
                            />
                          )}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.doseFrequency?.message}
                      </Typography>
                    </FormControl>
                  </Grid>
                  {["SOS"].includes(
                    methods.getValues("doseFrequency.value"),
                  ) && (
                    <>
                      <Grid
                        item
                        sm={6}
                        lg={8}
                        xl={9}
                        display={{
                          xs: "none",
                          sm: "grid",
                        }}
                      ></Grid>
                      <Grid item xs={12} sm={6} lg={4} xl={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.indicationOfSosMedicineId
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Indication for SOS medicine
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <Controller
                              control={methods.control}
                              name="indicationOfSosMedicineId"
                              render={({ field }) => (
                                <CustomAsyncSelect
                                  {...field}
                                  isNotCreatable={false}
                                  id={"indicationOfSosMedicineId"}
                                  FieldName={"indicationOfSosMedicineId"}
                                  defaultOptions={indicationForSosMedicineColl}
                                  openMenuOnFocus
                                  isClearable
                                  isSearchable
                                  loadOptions={loadIndicationForSosMedicineList}
                                  menuPortalTarget={document.querySelector(
                                    "body",
                                  )}
                                  handleChange={(e) => {
                                    if (e?.__isNew__ === true) {
                                      handleClickOpen(
                                        true,
                                        e.label,
                                        "MEDICINE_SOS",
                                        e,
                                      );
                                    } else if (
                                      methods
                                        ?.getValues()
                                        ?.indicationOfSosMedicineId?.value?.toString() !==
                                      e?.value?.toString()
                                    ) {
                                      methods.setValue(
                                        "indicationOfSosMedicineId",
                                        e?.value ? e : null,
                                        {
                                          shouldValidate: true,
                                        },
                                      );
                                      methods.setValue(
                                        "indicationOfSosMedicineName",
                                        e?.value ? e?.label : null,
                                        {
                                          shouldValidate: true,
                                        },
                                      );
                                    }
                                  }}
                                />
                              )}
                            />
                          </SkeletonWrapper>
                          <Typography className="validation-message">
                            {
                              methods.formState.errors.indicationOfSosMedicineId
                                ?.message
                            }
                          </Typography>
                        </FormControl>
                      </Grid>
                    </>
                  )}
                </Grid>
              </Grid>
              <Grid item xs={12} className="group-container">
                <Grid container item spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h6">Medicine Schedule</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} xl={2}>
                    <FormControlLabel
                      control={
                        <SkeletonWrapper
                          isLoading={isLoading}
                          className="skeleton-table-header-check-box"
                        >
                          <Checkbox
                            {...methods.register("isMorningScheduleEnabled", {
                              onChange: (e) => {
                                methods.setValue(
                                  "isMorningScheduleEnabled",
                                  e.target.checked,
                                  {
                                    shouldValidate: false,
                                  },
                                ); // UPDATE VALUE
                                methods.setValue("morningScheduleTime", null, {
                                  shouldValidate: true,
                                });
                                methods.trigger("doseFrequency");
                              },
                            })}
                            checked={
                              methods?.getValues()?.isMorningScheduleEnabled
                            }
                          />
                        </SkeletonWrapper>
                      }
                      label="Morning"
                    />
                  </Grid>
                  {methods.getValues()?.isMorningScheduleEnabled ? (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      display={{
                        md: "none",
                      }}
                    >
                      <FormControl>
                        <SkeletonWrapper
                          isLoading={isLoading}
                          className="skeleton-input"
                        >
                          <Controller
                            control={methods.control}
                            name="morningScheduleTime"
                            render={({ field }) => (
                              <CustomTimePicker
                                label={""}
                                {...field}
                                value={methods.getValues()?.morningScheduleTime}
                                onTimeChange={(newTime: Dayjs | null) => {
                                  methods.setValue(
                                    "morningScheduleTime",
                                    newTime,
                                    {
                                      shouldValidate: true,
                                    },
                                  );
                                }}
                                error={
                                  !!methods.formState.errors.morningScheduleTime
                                }
                              />
                            )}
                          />
                        </SkeletonWrapper>
                      </FormControl>
                    </Grid>
                  ) : (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      display={{
                        xs: "none",
                        sm: "grid",
                        md: "none",
                      }}
                    />
                  )}
                  <Grid item xs={12} sm={6} md={3} xl={2}>
                    <FormControlLabel
                      control={
                        <SkeletonWrapper
                          isLoading={isLoading}
                          className="skeleton-table-header-check-box"
                        >
                          <Checkbox
                            {...methods.register("isAfternoonScheduleEnabled", {
                              onChange: (e) => {
                                methods.setValue(
                                  "isAfternoonScheduleEnabled",
                                  e.target.checked,
                                ); // UPDATE VALUE
                                methods.setValue(
                                  "afternoonScheduleTime",
                                  null,
                                  { shouldValidate: true },
                                );
                                methods.trigger("doseFrequency");
                              },
                            })}
                            checked={
                              methods?.getValues()?.isAfternoonScheduleEnabled
                            }
                          />
                        </SkeletonWrapper>
                      }
                      label="Afternoon"
                    />
                  </Grid>
                  {methods.getValues()?.isAfternoonScheduleEnabled ? (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      display={{
                        md: "none",
                      }}
                    >
                      <FormControl>
                        <SkeletonWrapper
                          isLoading={isLoading}
                          className="skeleton-input"
                        >
                          <Controller
                            control={methods.control}
                            name="afternoonScheduleTime"
                            render={({ field }) => (
                              <CustomTimePicker
                                label={""}
                                {...field}
                                value={
                                  methods.getValues()?.afternoonScheduleTime
                                }
                                onTimeChange={(newTime: Dayjs | null) => {
                                  methods.setValue(
                                    "afternoonScheduleTime",
                                    newTime,
                                    {
                                      shouldValidate: true,
                                    },
                                  );
                                }}
                                error={
                                  !!methods.formState.errors
                                    .afternoonScheduleTime
                                }
                              />
                            )}
                          />
                        </SkeletonWrapper>
                      </FormControl>
                    </Grid>
                  ) : (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      display={{
                        xs: "none",
                        sm: "grid",
                        md: "none",
                      }}
                    />
                  )}
                  <Grid item xs={12} sm={6} md={3} xl={2}>
                    <FormControlLabel
                      control={
                        <SkeletonWrapper
                          isLoading={isLoading}
                          className="skeleton-table-header-check-box"
                        >
                          <Checkbox
                            {...methods.register("isEveningScheduleEnabled", {
                              onChange: (e) => {
                                methods.setValue(
                                  "isEveningScheduleEnabled",
                                  e.target.checked,
                                ); // UPDATE VALUE
                                methods.setValue("eveningScheduleTime", null, {
                                  shouldValidate: true,
                                });
                                methods.trigger("doseFrequency");
                              },
                            })}
                            checked={
                              methods?.getValues()?.isEveningScheduleEnabled
                            }
                          />
                        </SkeletonWrapper>
                      }
                      label="Evening"
                    />
                  </Grid>
                  {methods.getValues()?.isEveningScheduleEnabled ? (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      display={{
                        md: "none",
                      }}
                    >
                      <FormControl>
                        <SkeletonWrapper
                          isLoading={isLoading}
                          className="skeleton-input"
                        >
                          <Controller
                            control={methods.control}
                            name="eveningScheduleTime"
                            render={({ field }) => (
                              <CustomTimePicker
                                label={""}
                                {...field}
                                value={methods.getValues()?.eveningScheduleTime}
                                onTimeChange={(newTime: Dayjs | null) => {
                                  methods.setValue(
                                    "eveningScheduleTime",
                                    newTime,
                                    {
                                      shouldValidate: true,
                                    },
                                  );
                                }}
                                error={
                                  !!methods.formState.errors.eveningScheduleTime
                                }
                              />
                            )}
                          />
                        </SkeletonWrapper>
                      </FormControl>
                    </Grid>
                  ) : (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      display={{
                        xs: "none",
                        sm: "grid",
                        md: "none",
                      }}
                    />
                  )}
                  <Grid item xs={12} sm={6} md={3} xl={2}>
                    <FormControlLabel
                      control={
                        <SkeletonWrapper
                          isLoading={isLoading}
                          className="skeleton-table-header-check-box"
                        >
                          <Checkbox
                            {...methods.register("isNightScheduleEnabled", {
                              onChange: (e) => {
                                methods.setValue(
                                  "isNightScheduleEnabled",
                                  e.target.checked,
                                ); // UPDATE VALUE
                                methods.setValue("nightScheduleTime", null, {
                                  shouldValidate: true,
                                });
                                methods.trigger("doseFrequency");
                              },
                            })}
                            checked={
                              methods?.getValues()?.isNightScheduleEnabled
                            }
                          />
                        </SkeletonWrapper>
                      }
                      label="Night"
                    />
                  </Grid>
                  {methods.getValues()?.isNightScheduleEnabled ? (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      display={{
                        md: "none",
                      }}
                    >
                      <FormControl>
                        <SkeletonWrapper
                          isLoading={isLoading}
                          className="skeleton-input"
                        >
                          <Controller
                            control={methods.control}
                            name="nightScheduleTime"
                            render={({ field }) => (
                              <CustomTimePicker
                                label={""}
                                {...field}
                                value={methods.getValues()?.nightScheduleTime}
                                onTimeChange={(newTime: Dayjs | null) => {
                                  methods.setValue(
                                    "nightScheduleTime",
                                    newTime,
                                    {
                                      shouldValidate: true,
                                    },
                                  );
                                }}
                                error={
                                  !!methods.formState.errors.nightScheduleTime
                                }
                              />
                            )}
                          />
                        </SkeletonWrapper>
                      </FormControl>
                    </Grid>
                  ) : (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      display={{
                        xs: "none",
                        sm: "grid",
                        md: "none",
                      }}
                    />
                  )}
                  <Grid
                    item
                    xl={4}
                    display={{
                      xs: "none",
                      xl: "grid",
                    }}
                  />
                  {methods.getValues()?.isMorningScheduleEnabled ? (
                    <Grid
                      item
                      md={3}
                      xl={2}
                      display={{
                        xs: "none",
                        md: "grid",
                      }}
                    >
                      <FormControl fullWidth>
                        <SkeletonWrapper
                          isLoading={isLoading}
                          className="skeleton-input"
                        >
                          <Controller
                            control={methods.control}
                            name="morningScheduleTime"
                            render={({ field }) => (
                              <CustomTimePicker
                                label={""}
                                {...field}
                                value={methods.getValues()?.morningScheduleTime}
                                onTimeChange={(newTime: Dayjs | null) => {
                                  methods.setValue(
                                    "morningScheduleTime",
                                    newTime,
                                    {
                                      shouldValidate: true,
                                    },
                                  );
                                }}
                                error={
                                  !!methods.formState.errors.morningScheduleTime
                                }
                              />
                            )}
                          />
                        </SkeletonWrapper>
                      </FormControl>
                    </Grid>
                  ) : (
                    <Grid
                      item
                      md={3}
                      xl={2}
                      display={{
                        xs: "none",
                        md: "grid",
                      }}
                    />
                  )}
                  {methods.getValues()?.isAfternoonScheduleEnabled ? (
                    <Grid
                      item
                      md={3}
                      xl={2}
                      display={{
                        xs: "none",
                        md: "grid",
                      }}
                    >
                      <FormControl fullWidth>
                        <SkeletonWrapper
                          isLoading={isLoading}
                          className="skeleton-input"
                        >
                          <Controller
                            control={methods.control}
                            name="afternoonScheduleTime"
                            render={({ field }) => (
                              <CustomTimePicker
                                label={""}
                                {...field}
                                value={
                                  methods.getValues()?.afternoonScheduleTime
                                }
                                onTimeChange={(newTime: Dayjs | null) => {
                                  methods.setValue(
                                    "afternoonScheduleTime",
                                    newTime,
                                    {
                                      shouldValidate: true,
                                    },
                                  );
                                }}
                                error={
                                  !!methods.formState.errors
                                    .afternoonScheduleTime
                                }
                              />
                            )}
                          />
                        </SkeletonWrapper>
                      </FormControl>
                    </Grid>
                  ) : (
                    <Grid
                      item
                      md={3}
                      xl={2}
                      display={{
                        xs: "none",
                        md: "grid",
                      }}
                    />
                  )}
                  {methods.getValues()?.isEveningScheduleEnabled ? (
                    <Grid
                      item
                      md={3}
                      xl={2}
                      display={{
                        xs: "none",
                        md: "grid",
                      }}
                    >
                      <FormControl fullWidth>
                        <SkeletonWrapper
                          isLoading={isLoading}
                          className="skeleton-input"
                        >
                          <Controller
                            control={methods.control}
                            name="eveningScheduleTime"
                            render={({ field }) => (
                              <CustomTimePicker
                                label={""}
                                {...field}
                                value={methods.getValues()?.eveningScheduleTime}
                                onTimeChange={(newTime: Dayjs | null) => {
                                  methods.setValue(
                                    "eveningScheduleTime",
                                    newTime,
                                    {
                                      shouldValidate: true,
                                    },
                                  );
                                }}
                                error={
                                  !!methods.formState.errors.eveningScheduleTime
                                }
                              />
                            )}
                          />
                        </SkeletonWrapper>
                      </FormControl>
                    </Grid>
                  ) : (
                    <Grid
                      item
                      md={3}
                      xl={2}
                      display={{
                        xs: "none",
                        md: "grid",
                      }}
                    />
                  )}
                  {methods.getValues()?.isNightScheduleEnabled ? (
                    <Grid
                      item
                      md={3}
                      xl={2}
                      display={{
                        xs: "none",
                        md: "grid",
                      }}
                    >
                      <FormControl fullWidth>
                        <SkeletonWrapper
                          isLoading={isLoading}
                          className="skeleton-input"
                        >
                          <Controller
                            control={methods.control}
                            name="nightScheduleTime"
                            render={({ field }) => (
                              <CustomTimePicker
                                label={""}
                                {...field}
                                value={methods.getValues()?.nightScheduleTime}
                                onTimeChange={(newTime: Dayjs | null) => {
                                  methods.setValue(
                                    "nightScheduleTime",
                                    newTime,
                                    {
                                      shouldValidate: true,
                                    },
                                  );
                                }}
                                error={
                                  !!methods.formState.errors.nightScheduleTime
                                }
                              />
                            )}
                          />
                        </SkeletonWrapper>
                      </FormControl>
                    </Grid>
                  ) : (
                    <Grid
                      item
                      md={3}
                      xl={2}
                      display={{
                        xs: "none",
                        md: "grid",
                      }}
                    />
                  )}
                  <Grid
                    item
                    xl={4}
                    display={{
                      xs: "none",
                      xl: "grid",
                    }}
                  />
                  {/* <Grid item xs={12} md={8} xl={6}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${methods.formState.errors.scheduleInstruction
                          ? "validation-message"
                          : ""
                          }`}
                      >
                        Schedule Instruction
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input"
                      >
                        <CustomTextField
                          placeholder="Ex. Sunday, Monday..."
                          autoCapitalize="none"
                          type="text"
                          fullWidth
                          id="scheduleInstruction"
                          multiline
                          rows={2}
                          {...methods.register("scheduleInstruction")}
                          error={!!methods.formState.errors.scheduleInstruction}
                          className={`text-input ${methods.formState.errors.scheduleInstruction
                            ? "error"
                            : ""
                            }`}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.scheduleInstruction?.message}
                      </Typography>
                    </FormControl>
                  </Grid> */}
                </Grid>
              </Grid>
              <Grid item xs={12} className="group-container">
                <Grid container item spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h6">Instruction(s)</Typography>
                  </Grid>
                  <Grid item xs={12} lg={6}>
                    <Box
                      className="template-table-container"
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        border: "1px solid #0000001f",
                        borderRadius: 4,
                      }}
                    >
                      <Box className="inner-title-container">
                        <Box
                          className="template-box-sm"
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            backgroundColor: "#f1f5f8",
                          }}
                        >
                          <Typography>Medicine Instruction(s)</Typography>
                        </Box>
                        <DraggableList
                          items={
                            methods
                              ?.getValues()
                              ?.medicineDoseInstructionColl.filter(
                                (x) => x.isDeleted === false && x.mode !== "D",
                              ) || []
                          }
                          setItem={handleMedicineInstructionOrderChange}
                          Styledcard={InstructionCard}
                        />
                        <Box
                          style={{
                            padding: 10,
                            paddingLeft: 20,
                            paddingRight: 20,
                          }}
                        >
                          <FormControl fullWidth>
                            <SimpleAsyncSelect
                              placeholder=""
                              id={"MedicineInstructionSearch"}
                              FieldName={"MedicineInstruction"}
                              menuPortalTarget={document.querySelector("body")}
                              handleChange={(e) => {
                                if (e) {
                                  if (e?.__isNew__ === true) {
                                    handleClickOpen(
                                      true,
                                      e.label,
                                      "MEDICINE_INSTRUCTION",
                                      e,
                                    );
                                  } else {
                                    handleInstructionAdd(e);
                                  }
                                }
                              }}
                              value={null}
                              loadOptions={loadMedicineInstructionList}
                              defaultOptions={instructionColl}
                              CustomOptionComponent={
                                medicineInstructionOptionComponent
                              }
                              isSearchable
                              isNotCreatable={false}
                            />
                          </FormControl>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: smd ? "flex" : "none",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Box>
                    {(methods.getValues("previousMedicineDoseId") ?? 0) > 0 && (
                      <Box className="previous-next-button">
                        <Tooltip title="Previous" placement="top">
                          <IconButton
                            disableRipple
                            onClick={() => {
                              handlePreviousOrNExtMedicineDose(
                                methods.getValues("previousMedicineDoseId"),
                              );
                            }}
                          >
                            <ArrowBackIosNewRoundedIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </Box>
                  <Box>
                    {(methods.getValues("nextMedicineDoseId") ?? 0) > 0 && (
                      <Box className="previous-next-button">
                        <Tooltip title="Next" placement="top">
                          <IconButton
                            disableRipple
                            onClick={() => {
                              handlePreviousOrNExtMedicineDose(
                                methods.getValues("nextMedicineDoseId"),
                              );
                            }}
                          >
                            <ArrowForwardIosRoundedIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  {!isForPopUp && (
                    <Box
                      sx={{
                        display: smd ? "none" : "block",
                      }}
                    >
                      {(methods.getValues("previousMedicineDoseId") ?? 0) >
                        0 && (
                        <Box className="previous-next-button">
                          <Tooltip title="Previous" placement="top">
                            <IconButton
                              disableRipple
                              onClick={() => {
                                handlePreviousOrNExtMedicineDose(
                                  methods.getValues("previousMedicineDoseId"),
                                );
                              }}
                            >
                              <ArrowBackIosNewRoundedIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>
                  )}
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      width: "100%",
                      justifyContent: "end",
                      gap: 2,
                    }}
                  >
                    {(methods.getValues("previousMedicineDoseId") ?? 0) > 0 &&
                      !isForPopUp && (
                        <LoadingButton
                          color="primary"
                          startIcon={<></>}
                          variant="contained"
                          type="submit"
                          className="primary-button"
                          name="save"
                          onClick={(e) => {
                            setParam({ ...param, saveType: "SAVE_&_PREVIOUS" });
                          }}
                          disabled={isLoading || isLoading}
                        >
                          {(medicineDoseId > 0 ? "Update" : "Save") +
                            " & Previous "}
                        </LoadingButton>
                      )}
                    {!isForPopUp && (
                      <LoadingButton
                        color="primary"
                        loadingPosition="start"
                        startIcon={<></>}
                        variant="contained"
                        type="submit"
                        className="primary-button"
                        name="save"
                        onClick={(e) => {
                          setParam({ ...param, saveType: "SAVE_&_COPY" });
                        }}
                        disabled={isLoading || isLoading}
                      >
                        {(medicineDoseId > 0 ? "Update" : "Save") + " & Copy"}
                      </LoadingButton>
                    )}
                    <LoadingButton
                      color="primary"
                      loadingPosition="start"
                      startIcon={<></>}
                      variant="contained"
                      type="submit"
                      className="primary-button"
                      onClick={(e) => {
                        setParam({ ...param, saveType: "SAVE" });
                      }}
                      name="save"
                      disabled={isLoading || isLoading}
                    >
                      {medicineDoseId > 0 ? "Update" : "Save"}
                    </LoadingButton>
                    {(methods.getValues("nextMedicineDoseId") ?? 0) > 0 &&
                      !isForPopUp && (
                        <LoadingButton
                          color="primary"
                          loadingPosition="start"
                          startIcon={<></>}
                          variant="contained"
                          type="submit"
                          className="primary-button"
                          name="save"
                          onClick={(e) => {
                            setParam({ ...param, saveType: "SAVE_&_NEXT" });
                          }}
                          disabled={isLoading || isLoading}
                        >
                          {(medicineDoseId > 0 ? "Update" : "Save") + " & Next"}
                        </LoadingButton>
                      )}
                    <LoadingButton
                      color="primary"
                      loadingPosition="start"
                      startIcon={<></>}
                      variant="outlined"
                      type="button"
                      className="secondary-button"
                      name="cancel"
                      onClick={handleBack}
                    >
                      Cancel
                    </LoadingButton>
                    {!isForPopUp && (
                      <Box
                        sx={{
                          display: smd ? "none" : "block",
                        }}
                      >
                        {(methods.getValues("nextMedicineDoseId") ?? 0) > 0 && (
                          <Box className="previous-next-button">
                            <Tooltip title="Next" placement="top">
                              <IconButton
                                disableRipple
                                onClick={() => {
                                  handlePreviousOrNExtMedicineDose(
                                    methods.getValues("nextMedicineDoseId"),
                                  );
                                }}
                              >
                                <ArrowForwardIosRoundedIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
        {/* MEDICINE_USED_AS */}
        <Dialog
          className="dialogPage"
          open={customPopUp.open && customPopUp.code === "MEDICINE_USED_AS"}
          onClose={(e, reason) => {
            if (reason !== "backdropClick") {
              handleClose();
            }
          }}
          fullScreen
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          PaperProps={{
            style: {
              width: "600px",
              height: "350px",
              maxWidth: "none",
            },
          }}
        >
          <AppBar sx={{ position: "relative" }}>
            <Toolbar>
              <Typography
                style={{ fontWeight: 600, marginLeft: 0 }}
                sx={{ ml: 2, flex: 1 }}
                variant="h6"
              >
                Save Medicine Used As
              </Typography>
            </Toolbar>
            <Grid item xs={12}>
              <Divider />
            </Grid>
          </AppBar>
          <DialogContent sx={{ padding: "12px 24px" }}>
            <MedicineUsedAsSave
              handleClosePopUp={(code, company) => {
                handleManagePopup(code, company);
              }}
              isForPopUp={customPopUp.open}
              name={customPopUp.name}
            />
          </DialogContent>
          <Divider />
        </Dialog>
        {/* RELATION_TO_FOOD */}
        <Dialog
          className="dialogPage"
          open={customPopUp.open && customPopUp.code === "RELATION_TO_FOOD"}
          onClose={(e, reason) => {
            if (reason !== "backdropClick") {
              handleClose();
            }
          }}
          fullScreen
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          PaperProps={{
            style: {
              width: "600px",
              height: "500px",
              maxWidth: "none",
            },
          }}
        >
          <AppBar sx={{ position: "relative" }}>
            <Toolbar>
              <Typography
                style={{ fontWeight: 600, marginLeft: 0 }}
                sx={{ ml: 2, flex: 1 }}
                variant="h6"
              >
                Save Medicine Relation To Food/Sleep
              </Typography>
            </Toolbar>
            <Grid item xs={12}>
              <Divider />
            </Grid>
          </AppBar>
          <DialogContent sx={{ padding: "12px 24px" }}>
            <MedicineRelationToFoodSave
              handleClosePopUp={(code, company) => {
                handleManagePopup(code, company);
              }}
              isForPopUp={customPopUp.open}
              name={customPopUp.name}
            />
          </DialogContent>
          <Divider />
        </Dialog>
        {/* MEDICINE_DILUENT */}
        <Dialog
          className="dialogPage"
          open={customPopUp.open && customPopUp.code === "MEDICINE_DILUENT"}
          onClose={(e, reason) => {
            if (reason !== "backdropClick") {
              handleClose();
            }
          }}
          fullScreen
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          PaperProps={{
            style: {
              width: "650px",
              height: "400px",
              maxWidth: "none",
            },
          }}
        >
          <AppBar sx={{ position: "relative" }}>
            <Toolbar>
              <Typography
                style={{ fontWeight: 600, marginLeft: 0 }}
                sx={{ ml: 2, flex: 1 }}
                variant="h6"
              >
                Save Medicine Diluent Name
              </Typography>
            </Toolbar>
            <Grid item xs={12}>
              <Divider />
            </Grid>
          </AppBar>
          <DialogContent sx={{ padding: "12px 24px" }}>
            <MedicineDiluentNameSave
              handleClosePopUp={(code, company) => {
                handleManagePopup(code, company);
              }}
              isForPopUp={customPopUp.open}
              name={customPopUp.name}
            />
          </DialogContent>
          <Divider />
        </Dialog>
        {/* MEDICINE_ROUTE */}
        <Dialog
          className="dialogPage"
          open={customPopUp.open && customPopUp.code === "MEDICINE_ROUTE"}
          onClose={(e, reason) => {
            if (reason !== "backdropClick") {
              handleClose();
            }
          }}
          fullScreen
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          PaperProps={{
            style: {
              width: "600px",
              height: "500px",
              maxWidth: "none",
            },
          }}
        >
          <AppBar sx={{ position: "relative" }}>
            <Toolbar>
              <Typography
                style={{ fontWeight: 600, marginLeft: 0 }}
                sx={{ ml: 2, flex: 1 }}
                variant="h6"
              >
                Save Medicine Route Of Administration
              </Typography>
            </Toolbar>
            <Grid item xs={12}>
              <Divider />
            </Grid>
          </AppBar>
          <DialogContent sx={{ padding: "12px 24px" }}>
            <MedicineRouteOfAdministrationSave
              handleClosePopUp={(code, company) => {
                handleManagePopup(code, company);
              }}
              isForPopUp={customPopUp.open}
              name={customPopUp.name}
            />
          </DialogContent>
          <Divider />
        </Dialog>
        {/* MEDICINE_SOS */}
        <Dialog
          className="dialogPage"
          open={customPopUp.open && customPopUp.code === "MEDICINE_SOS"}
          onClose={(e, reason) => {
            if (reason !== "backdropClick") {
              handleClose();
            }
          }}
          fullScreen
          PaperProps={{
            style: {
              width: "650px",
              height: "400px",
              maxWidth: "none",
            },
          }}
        >
          <AppBar sx={{ position: "relative" }}>
            <Toolbar>
              <Typography
                style={{ fontWeight: 600, marginLeft: 0 }}
                sx={{ ml: 2, flex: 1 }}
                variant="h6"
              >
                Save Medicine Indication For SOS
              </Typography>
            </Toolbar>
            <Grid item xs={12}>
              <Divider />
            </Grid>
          </AppBar>
          <DialogContent sx={{ padding: "12px 24px" }}>
            <MedicineIndicationForSOSMedicineSave
              handleClosePopUp={(code, company) => {
                handleManagePopup(code, company);
              }}
              isForPopUp={customPopUp.open}
              name={customPopUp.name}
            />
          </DialogContent>
          <Divider />
        </Dialog>
        {/* MEDICINE_GENERIC_NAME */}
        <Dialog
          className="dialogPage"
          open={
            customPopUp.open && customPopUp.code === "MEDICINE_GENERIC_NAME"
          }
          onClose={(e, reason) => {
            if (reason !== "backdropClick") {
              handleClose();
            }
          }}
          fullScreen
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          PaperProps={{
            style: {
              width: "650px",
              height: "400px",
              maxWidth: "none",
            },
          }}
        >
          <AppBar sx={{ position: "relative" }}>
            <Toolbar>
              <Typography
                style={{ fontWeight: 600, marginLeft: 0 }}
                sx={{ ml: 2, flex: 1 }}
                variant="h6"
              >
                Save Medicine Generic Name
              </Typography>
            </Toolbar>
            <Grid item xs={12}>
              <Divider />
            </Grid>
          </AppBar>
          <DialogContent sx={{ padding: "12px 24px" }}>
            <MedicineGenericNameSave
              handleClosePopUp={(code, company) => {
                handleManagePopup(code, company);
              }}
              isForPopUp={customPopUp.open}
              name={customPopUp.name}
            />
          </DialogContent>
          <Divider />
        </Dialog>
        <Dialog
          className="dialogPage"
          open={customPopUp.open && customPopUp.code === "MEDICINE"}
          onClose={(e, reason) => {
            if (reason !== "backdropClick") {
              handleClose();
            }
          }}
          fullScreen
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          PaperProps={{
            style: {
              width: "1200px",
              height: "700px",
              maxWidth: "none",
            },
          }}
        >
          <AppBar sx={{ position: "relative" }}>
            <Toolbar>
              <Typography
                style={{ fontWeight: 600, marginLeft: 0 }}
                sx={{ ml: 2, flex: 1 }}
                variant="h6"
              >
                {customPopUp?.medicineId > 0 ? "Update" : "Save"} Medicine
              </Typography>
            </Toolbar>
            <Grid item xs={12}>
              <Divider />
            </Grid>
          </AppBar>
          <DialogContent sx={{ padding: "12px 24px" }}>
            <Medicine2Save
              handleClosePopUp={(code, company) => {
                handleManagePopup(code, company);
              }}
              isForPopUp={customPopUp.open}
              name={customPopUp.name}
              genericNameObject={customPopUp.genericNameObject}
              isFrom="MEDICINE_DOSE"
              popUpMedicineId={customPopUp?.medicineId || 0}
            />
          </DialogContent>
          <Divider />
        </Dialog>

        {/* MEDICINE_INSTRUCTION */}
        <Dialog
          className="dialogPage"
          open={customPopUp.open && customPopUp.code === "MEDICINE_INSTRUCTION"}
          onClose={(e, reason) => {
            if (reason !== "backdropClick") {
              handleClose();
            }
          }}
          fullScreen
          PaperProps={{
            style: {
              width: "1200px",
              height: "650px",
              maxWidth: "none",
            },
          }}
        >
          <AppBar sx={{ position: "relative" }}>
            <Toolbar>
              <Typography
                style={{ fontWeight: 600, marginLeft: 0 }}
                sx={{ ml: 2, flex: 1 }}
                variant="h6"
              >
                Save Medicine instructions
              </Typography>
            </Toolbar>
            <Grid item xs={12}>
              <Divider />
            </Grid>
          </AppBar>
          <DialogContent sx={{ padding: "12px 24px" }}>
            <InstructionSave
              handleClosePopUp={(code, company) => {
                handleManagePopup(code, company);
              }}
              isForPopUp={customPopUp.open}
              name={customPopUp.name}
              instructionType={"medicine"}
            />
          </DialogContent>
          <Divider />
        </Dialog>
      </Box>
    </Box>
  );
}
